// b3x-in 웹(packages/web) 화면 문구의 유일한 자리 — 공통 @mops/i18n-v1 README 「사전 파일 규약」.
//
// - 화면 코드에 한글 문장을 쓰지 않는다 — 여기에 키를 더하고 `tx("키")` 로 꺼낸다(`t` 가 아닌 이유는 ./index.ts).
// - 키는 원문이 아니라 자리 이름(`<화면·영역>.<자리>`). 같은 문장을 두 키로 만들지 않는다.
// - 문체는 합니다체(공통 COMMON.md 「화면 문구」). 오류는 두 문장 — 무엇이 안 됐는지 + 무엇을 하면 되는지.
// - 상태 단어·단추 동사는 `common.*`(commonKo)를 쓴다. 같은 낱말을 여기 다시 적지 않는다.
// - **서버가 준 문장(거절·오류)은 여기 옮기지 않는다** — 화면과 MCP 가 같은 문자열을 읽으므로
//   화면은 서버의 `message` 를 그대로 보인다(AGENTS.md 「MCP」 · 「디자인」). 여기 있는 것은 서버가
//   문장을 안 줄 때(연결 실패 등)의 화면 몫뿐이다.
// - 객체 리터럴만 둔다(검사기 check-copy.mjs 가 이 모양을 읽는다). `as const` 를 떼지 않는다 —
//   떼면 `{자리}` 이름을 타입이 모른다.
import { commonKo } from "@mops/i18n-v1";

export const ko = {
  ...commonKo,

  /** 여러 화면이 함께 쓰는 낱말. */
  field: {
    username: "아이디",
    password: "비밀번호",
    name: "이름",
    role: "역할",
    status: "상태",
  },

  /** 공통 부품(Card · Page)의 기본 글자. */
  ui: {
    statTitle: "상태",
    help: "설명",
    crumbs: "경로",
    alertHead: {
      success: "됨",
      warning: "주의",
      error: "오류",
      info: "알림",
    },
  },

  /** 이슈 단계 — 상태 표식(StatusPill)과 보드 칸이 함께 읽는다. 건강 상태 고정어(common.status)와 다른 축이다. */
  issue: {
    status: {
      todo: "할 일",
      ready: "대기",
      inProgress: "진행",
      review: "검토",
      blocked: "막힘",
      done: "완료",
      cancelled: "취소",
    },
    /** 「도는 중」은 열린 세션이다 — 상태가 아니다. */
    working: "도는 중",
    workingLabel: "{label} · 도는 중",
    workingTip: "이 이슈에 열린 세션이 있습니다",
    held: "나루 할당",
    heldShort: "나루",
    merging: "병합 대기",
    noType: "유형 없음",
    thisEpic: "이 에픽",
    epicOnly: "{title}의 하위만 보기",
    doneOf: "{done} / {total} 완료",
    ago: "{when} 전",
    /** 열린 지 얼마나 됐나 — 「도는 중 · 12분째」. */
    since: {
      now: "방금",
      min: "{n}분째",
      hour: "{n}시간째",
      day: "{n}일째",
    },
    /** 「째」 없는 경과 — 「3시간 전」의 앞쪽, 걸린 시간. */
    span: {
      min: "{n}분",
      hour: "{n}시간",
      day: "{n}일",
    },
    priority: {
      critical: "심각",
      high: "우선",
      normal: "일반",
    },
    complexity: {
      deep: "심화",
      hard: "어려움",
      normal: "보통",
      light: "단순",
    },
  },

  /** 상단 바 · 서랍 · 폰 하단 탭바. */
  nav: {
    dashboard: "대시보드",
    board: "이슈 보드",
    milestones: "마일스톤",
    projects: "프로젝트",
    tidy: "이슈 정리",
    chores: "게시판",
    orgs: "조직",
    conn: "연결 설정",
    git: "git 저장소",
    code: "코드 분석",
    queue: "대기열",
    graph: "그래프",
    help: "도움말",
    history: "이력",
    session: "세션 진행",
    logs: "로그",
    patterns: "디자인 패턴",
    servers: "서버",
    settings: "설정",
    alarms: "알람",
    group: {
      dash: "대시보드",
      work: "일감",
      obs: "관측",
      admin: "관리",
      patterns: "디자인 패턴",
    },
    label: "주 메뉴",
    labelPhone: "주 메뉴(폰)",
    labelAll: "전체 메뉴",
    openMenu: "메뉴 열기",
    closeMenu: "메뉴 닫기",
    side: "화면 메뉴",
    sideCollapse: "메뉴 접기",
    sideExpand: "메뉴 펼치기",
    theme: {
      label: "테마",
      current: "테마 · {name}",
      system: "시스템",
      light: "밝게",
      dark: "어둡게",
    },
    chat: {
      button: "채팅",
      label: "채팅 겉창",
      labelRunning: "채팅 겉창 · 도는 세션 {n}",
      running: "도는 세션 {n}",
      openTip: "채팅 겉창 열기 — 세션이 무엇을 하는지 보고, 멈추고, 말을 겁니다",
      closeTip: "채팅 겉창 닫기 (Esc)",
    },
  },

  /** 상단 바 「내 차례」 서랍 — 사람이 답할 질문 · 내가 맡아 둔 이슈. */
  myturn: {
    title: "내 차례",
    buttonN: "내 차례 {n}건",
    count: "{n}건",
    loadFailed: "내 차례를 불러오지 못했습니다 — {err}. 다시 시도해 주세요.",
    empty: "지금 내 차례인 이슈가 없습니다",
    emptyDesc: "세션이 질문을 남기거나 이슈를 맡아 두면 여기에 섭니다.",
    questions: "답할 질문 {n}",
    mine: "내가 맡아 둔 이슈 {n}",
  },

  /** 대시보드(#/) — 아직 뼈대 확인만 있다. */
  dash: {
    lead: "지금 전체가 어떤 상태인지 한눈에 봅니다.",
    apiDown: "API 에 닿지 못했습니다 — {err}. api 가 떠 있는지({url}) 확인해 주세요.",
    apiOk: "정상 · v{version}",
    apiFail: "못 닿음 — {err}",
    apiChecking: "확인 중…",
    serverAddons: "서버 애드온",
    webAddons: "화면 애드온",
    coreOnly: "없음(코어만)",
  },

  /** 일감 › 마일스톤(t-in-port-work-milestones) — 고르기 · 새로 만들기 · 이슈 넣기/빼기. */
  milestones: {
    lead: "마일스톤을 고르고 이슈를 여러 개 체크해 한 번에 넣거나 뺍니다.",
    rail: "마일스톤",
    railNote: "{n}개",
    pick: "마일스톤 고르기",
    new: "새 마일스톤",
    loadFailed: "마일스톤을 불러오지 못했습니다 — {err}. 새로고침해 주세요.",
    none: "마일스톤을 골라 주세요",
    noneDesc: "왼쪽 목록(폰은 위 고르기)에서 고른 뒤, 이슈를 여러 개 체크해 한 번에 넣거나 뺄 수 있습니다.",
    notFound: "마일스톤 {id} 를 찾지 못했습니다. 지워졌거나 주소가 틀렸습니다.",
    noGoal: "목표가 적혀 있지 않습니다.",
    progress: "완료 / 전체 이슈",
    inMs: "이 마일스톤의 이슈 {n}",
    inMsEmpty: "아직 든 이슈가 없습니다",
    cand: "넣을 수 있는 이슈 {n}",
    candAll: "모든 프로젝트",
    candEmpty: "마일스톤 없이 남은 열린 이슈가 없습니다",
    add: "넣기 {n}",
    drop: "빼기 {n}",
    form: {
      title: "새 마일스톤",
      name: "제목",
      id: "id(선택)",
      idHint: "비우면 서버가 만듭니다. 영어 소문자 · 숫자 · 하이픈.",
      idBad: "영어 소문자 · 숫자 · 하이픈만 쓸 수 있습니다.",
      goal: "목표(선택)",
      project: "프로젝트",
      create: "만들기",
      failed: "만들지 못했습니다 — {err}",
    },
  },

  /** 대시보드의 pr 포팅 조각(t-in-port-dashboard) — 지표 · 나루 · 진행률 · 주의 필요 · 프로젝트 카드. */
  dashx: {
    panel: "지금 상태",
    tab: { kpi: "지표", timeline: "타임라인", naru: "나루" },
    updated: "{t} 갱신",
    fold: "판 접기",
    unfold: "판 펴기",
    loadFailed: "이슈 목록을 불러오지 못했습니다 — {err}. 새로고침해 주세요.",
    scope: "프로젝트 고르기",
    allProjects: "전체 프로젝트",
    system: "시스템",
    kpi: {
      open: "열린 이슈",
      running: "도는 세션",
      blocked: "막힘",
      questions: "답할 질문",
      review: "검토 대기",
      merging: "병합 대기",
    },
    naru: {
      empty: "보이는 나루가 없습니다",
      adminOnly: "러너 목록은 관리자만 봅니다 — 지금은 세션이 도는 나루만 보입니다.",
      state: { running: "도는 중", idle: "노는 중", down: "끊김", never: "아직 안 붙음", unknown: "모름" },
      idleLine: "붙어 있고, 맡은 이슈가 없습니다.",
      noRun: "돌리는 이슈가 없습니다.",
      since: "기동 {t}",
      downFor: "끊긴 지 {t}",
      sinceUnknown: "기동 시각 모름",
    },
    progress: {
      label: "진행률(잎 이슈 · 취소 제외)",
      value: "{pct}% · {done}/{total}",
      none: "아직 셀 이슈가 없습니다.",
    },
    attention: {
      title: "주의 필요 · 막힘 · 질문 {n}건",
      empty: "사람 손을 기다리는 이슈가 없습니다",
      question: "질문 — {q}",
    },
    projects: {
      title: "프로젝트 {p}개 · 열린 이슈 {n}개",
      empty: "열린 이슈가 없습니다",
      emptyDesc: "보드에서 새 이슈를 만들면 여기 프로젝트별로 모입니다.",
      none: "미분류",
      open: "열림 {n}",
      group: { doing: "진행중 {n}", blocked: "막힘 {n}", todo: "할 일 {n}" },
      more: "{n}건 더 — 보드에서 보기",
    },
  },

  /** 사용이력 카드 — 30분 칸마다 어느 상태로 옮겨 간 이슈가 몇 건인가(docs/decisions/usage-history.md). */
  usage: {
    title: "사용이력",
    sub: "30분 칸마다 어느 상태로 옮겨 간 이슈가 몇 건인가 · 한국 시각",
    range: {
      "1d": "1일",
      day: "특정일",
      "7d": "7일",
      "30d": "30일",
    },
    rangeLabel: "기간",
    dayLabel: "볼 날짜",
    legendLabel: "상태 켜고 끄기",
    toggleTip: "누르면 이 상태를 차트에서 끄거나 켭니다",
    loading: "사용이력 불러오는 중…",
    chartLabel: "상태별 전이 누적 막대",
    total: "이 기간 합계",
    totalN: "합계 {n}건",
    empty: "이 기간에 상태가 바뀐 이슈가 없습니다",
    emptyDesc: "dbix 가 답했고 칸이 전부 0 입니다. 기간을 넓혀 주세요.",
    behind: "최근 {n}건이 아직 dbix 로 가지 않았습니다 — 끝 칸이 실제보다 적을 수 있습니다.",
    truncated: "dbix 가 행 상한에서 잘랐습니다 — 막대 일부가 빠졌을 수 있습니다.",
    notConfigured: "dbix 연결이 없습니다. 사용이력은 dbix(시계열 보관소)에서 읽습니다 — 연결 설정 › dbix 에 주소 · 조회 토큰 · 설치 이름을 넣어 주세요.",
    openConn: "연결 설정 열기",
    askAdmin: "관리자에게 알려 주세요.",
    unknown: {
      unreachable: "dbix 에 닿지 못해 모릅니다 — 0 건이 아닙니다. dbix 주소와 dbix 가 떠 있는지 확인해 주세요.",
      unauthorized: "dbix 가 조회 토큰을 거절해 모릅니다. 연결 설정 › dbix 의 조회 토큰(「조회 허용」을 켠 토큰)을 확인해 주세요.",
      noSpec: "dbix 에 사용이력 규격이 없어 모릅니다. dbix 의 수집 › 규격 › 밑값 가져오기에서 B3XIN_TASK_TRANSITION 을 들여 주세요.",
      other: "dbix 가 답을 못 줘 모릅니다({kind}). dbix 로그를 확인해 주세요.",
    },
    loadFailed: "사용이력을 읽지 못했습니다 — {err}. 새로고침해 주세요.",
  },

  /** 연결 설정 › dbix 절 — 사용이력을 보내고 읽는 연결(crates/api/src/dbix.rs). */
  dbix: {
    title: "dbix — 사용이력",
    lead: "이슈 상태 전이를 dbix(시계열 보관소)로 보내고, 사용이력 차트를 거기서 읽습니다. 비워 두면 보내지 않습니다.",
    address: "dbix 주소",
    addressHint: "dbix 웹 주소입니다. 이 뒤에 /ingest 와 /api/data/query 를 붙여 부릅니다.",
    install: "설치 이름",
    installHint: "이 b3x-in 을 가리키는 이름입니다. 한 dbix 에 여러 설치가 보낼 때 서로 섞이지 않게 합니다.",
    ingestToken: "수집 토큰",
    ingestHint: "dbix 수집 › 토큰에서 발급합니다. 허용 규격은 B3XIN_TASK_TRANSITION 하나면 됩니다.",
    queryToken: "조회 토큰",
    queryHint: "「조회 허용」을 켠 dbix 토큰입니다. 사용이력 차트가 이 토큰으로 읽습니다.",
    tokenSetPh: "저장됨 — 바꿀 때만 적어 주세요",
    tokenNonePh: "아직 없습니다",
    test: "연결 시험",
    reset: "처음부터 다시 보내기",
    resetConfirm: "한 번 더 눌러 주세요 — 지금까지의 이력을 처음부터 다시 보냅니다",
    resetHint: "dbix 를 갈아 끼웠을 때 씁니다. 같은 전이는 dbix 가 한 건으로 접습니다.",
    saved: "저장했습니다.",
    stat: {
      push: "보내기",
      pushOk: "정상",
      pushOff: "멈춤 — {missing} 이 비었습니다",
      behind: "뒤처짐",
      behindN: "{n}건",
      lastSent: "마지막 보냄",
      lastError: "마지막 실패",
      never: "아직 없음",
    },
    missing: {
      baseUrl: "주소",
      ingestToken: "수집 토큰",
      queryToken: "조회 토큰",
      install: "설치 이름",
    },
    fail: {
      unreachable: "닿지 못함",
      unauthorized: "토큰 거절",
      forbidden: "허용 규격 아님",
      partial: "일부만 받음",
      rejected: "거절",
      "http-error": "dbix 오류",
    },
    side: {
      ingest: "수집",
      query: "조회",
    },
    result: {
      ok: "됩니다",
      "no-token": "토큰이 비었습니다",
      unauthorized: "토큰을 거절했습니다 — 토큰을 확인해 주세요",
      forbidden: "이 토큰은 받을 수 없는 규격입니다 — 허용 규격을 확인해 주세요",
      "no-spec": "B3XIN_TASK_TRANSITION 규격이 없습니다 — dbix 에서 밑값을 가져와 켜 주세요",
      "bad-request": "dbix 가 요청을 거절했습니다",
      "http-error": "dbix 가 오류로 답했습니다 — dbix 로그를 확인해 주세요",
      unreachable: "닿지 못했습니다 — 주소와 dbix 가 떠 있는지 확인해 주세요",
      "no-address": "주소가 비었습니다 — 주소를 넣어 주세요",
    },
    loadFailed: "dbix 연결을 읽지 못했습니다 — {err}. 새로고침해 주세요.",
    saveFailed: "저장하지 못했습니다 — {err}. 다시 시도해 주세요.",
  },

  /** 아직 안 세운 화면 · 없는 주소. */
  notYet: {
    title: "없는 화면",
    empty: "이 화면은 아직 없습니다",
    desc: "M4 의 다음 이슈가 만듭니다.",
  },

  /** 새 버전 · 서버 연결 안내 — 공통 @mops/pwa-v1 의 PwaNotice 에 넘긴다(components/Pwa.tsx). */
  pwa: {
    update: "새 버전이 있습니다",
    unreachable: "서버에 연결하지 못했습니다",
  },

  /** 로그인 문지기 · 로그인 · 첫 설정 · hub(SSO). 거절 문장은 서버의 것을 그대로 보인다. */
  auth: {
    checking: "확인 중…",
    stateFailed: "로그인 상태를 읽지 못했습니다 — {error}. 「다시 시도」를 눌러 주세요.",
    sessionEnded: "세션이 끝났습니다. 다시 로그인해 주세요.",
    /** 연결 자체가 안 됐을 때 — 서버 문장이 없으니 어디에 못 닿았는지를 말한다. */
    unreachable: "서버({url})에 닿지 못했습니다 — {reason}. api 가 떠 있는지 확인해 주세요.",
    login: {
      hub: "MOPS 계정으로 로그인 (hub)",
      failed: "로그인 실패",
      noteLocal: "비밀번호를 잊었으면 관리자에게 말해 주세요 — 서버에서 임시 비밀번호를 냅니다(make password-reset).",
      ssoOnly: "이 설치는 MOPS 계정(hub)으로만 들어옵니다.",
      noteSso:
        "로컬 로그인은 닫혀 있습니다 — hub 의 2단계를 건너뛰는 길이 없습니다. hub 가 아예 죽었으면 서버에서 make auth-mode MODE=local+sso 로 열어 주세요.",
      noteBoth: "hub 의 2단계까지 거칩니다 — 이 설치의 기본 문입니다.",
      localOpen: "▾ 이 설치의 계정으로 로그인 (접기)",
      localClosed: "› 이 설치의 계정으로 로그인 (펼치기)",
      foldNote: "펼치면 아이디 · 비밀번호 폼이 이 자리에 열립니다.",
      /** 로그인 단추 위 체크 둘(공통 LoginRemember, auth.md §3 「로그인 유지」). */
      saveId: "아이디 저장",
      remember: "로그인 유지({days}일)",
    },
    setup: {
      title: "b3x-in 첫 설정",
      sub: "계정이 아직 없습니다 — 첫 관리자를 만듭니다",
      failed: "만들지 못했습니다",
      token: "설치 토큰",
      tokenHint: "서버에서만 볼 수 있습니다 — make auth-setup-token, 또는 api 부팅 로그 한 줄",
      confirm: "비밀번호 확인",
      mismatch: "위 비밀번호와 다릅니다. 같게 적어 주세요.",
      submit: "관리자 만들기",
      note: "하나가 생기면 이 화면은 닫힙니다(동시에 누른 쪽은 409 → 로그인 화면으로 갑니다).",
    },
    /** hub 에서 돌아오다 실패한 갈래(`sso_error`). 서버는 갈래만 싣고 문장은 여기서 쓴다. */
    sso: {
      state: {
        head: "hub 에서 돌아오다 멈췄습니다",
        body: "로그인 요청이 만료됐거나 다른 탭에서 시작됐습니다(state 가 안 맞음). 다시 누르면 처음부터 합니다.",
      },
      unreachable: {
        head: "hub 와 토큰을 바꾸지 못했습니다",
        body: "{hub} 에 이 서버가 닿지 못했습니다(코드 교환 단계). 서버 관리자에게 주소·방화벽 확인을 요청해 주세요.",
      },
      exchange: {
        head: "hub 가 코드 교환을 거절했습니다",
        body: "로그인이 만료됐거나 이 설치의 client 인증이 hub 와 안 맞습니다. 다시 해 보고, 계속되면 서버 관리자에게 api 로그 확인을 요청해 주세요.",
      },
      token: {
        head: "hub 의 응답을 받아들이지 않았습니다",
        body: "ID 토큰 검증(서명 · 발급자 · 대상)에 실패했습니다. 서버 관리자에게 api 로그와 B3XIN_SSO_ISSUER_URL 확인을 요청해 주세요.",
      },
      disabled: { body: "이 계정은 막혀 있습니다. 관리자에게 문의해 주세요." },
      setup: {
        head: "아직 첫 설정 전입니다",
        body: "이 설치에는 계정이 하나도 없습니다. 관리자가 설치 토큰으로 첫 관리자를 만든 뒤에 hub 로 들어올 수 있습니다.",
      },
      server: {
        head: "서버 오류",
        body: "계정을 잇거나 세션을 만들지 못했습니다. 서버 관리자에게 api 로그 확인을 요청해 주세요.",
      },
      unknownHead: "hub 로그인 실패",
    },
  },

  /** 「관리자」는 공통 common.role.admin 이다 — 이 설치에만 있는 역할 이름만 여기 둔다. */
  role: {
    user: "일반",
  },

  /** 내 계정(#/account) · 계정 메뉴 · 비밀번호 · 2단계. */
  account: {
    title: "내 계정 · 보안",
    lead: "이 브라우저로 들어온 사람의 계정입니다. 다른 사람의 계정은 설정 › 사용자에서 관리자가 봅니다.",
    button: "내 계정 · {name}",
    menu: "계정 메뉴",
    viaHub: "MOPS 계정(hub)",
    viaLocal: "이 설치 계정",
    logoutNote: "로그아웃은 이 브라우저의 세션만 끝냅니다.",
    session: "이번 세션",
    unnamed: "(안 적음)",
    totpOn: "등록됨",
    totpOff: "미등록",
    totpHub: "hub 가 묻습니다",
    tokens: {
      title: "토큰은 어디서 바꾸나",
      lead: "이 설치는 사람마다 벤더 토큰을 두지 않습니다. 세션은 설치에 하나 둔 연결로 돌고, 바꾸는 곳은 아래와 같습니다.",
      model: "모델(claude · codex · 로컬)",
      modelWhere: "ola-mops 연결 토큰이 모델을 고릅니다 — 연결 설정",
      git: "git(GitHub · Gitea)",
      gitWhere: "연결마다 링크 승인으로 받습니다 — git 저장소",
      figma: "Figma",
      figmaWhere: "설계 트랙 애드온의 설치 값입니다(서버 .env)",
      open: "열기",
      adminOnly: "바꾸는 것은 관리자가 합니다.",
    },
    sessionNote: "로그인 세션은 이 설치 계정이면 7일입니다. 다른 기기의 세션은 비밀번호를 바꾸면 끊깁니다.",
    door: {
      local: "이 설치 계정으로",
      hub: "MOPS 계정(hub)으로",
      unknown: "들어온 문 모름",
      noEnd: "{door} · 끝나는 때 모름",
      until: "{door} · {when} 까지",
    },
    password: {
      title: "비밀번호 바꾸기",
      none: "이 계정은 이 설치의 비밀번호가 없습니다 — MOPS 계정(hub)으로만 들어옵니다.",
      done: "바꿨습니다 — 다른 기기의 세션은 끊었습니다.",
      current: "지금 비밀번호",
      next: "새 비밀번호",
      confirm: "새 비밀번호 확인",
      mismatch: "위 새 비밀번호와 다릅니다. 같게 적어 주세요.",
      submit: "바꾸기",
    },
  },

  /** 2단계 인증의 바깥 틀. 안쪽 문장(거절 포함)은 공통 @mops/totp-ui-v2 의 것이고 hub·b3x-pr·mops-code 와 바이트가 같다. */
  totp: {
    title: "2단계 인증",
    enrollNeeded: "2단계 인증 등록이 필요합니다",
    policyTitle: "2단계 인증 강제",
    policyNote: "이 설치 계정으로 들어오는 로그인에만 걸립니다 — hub 로 들어오는 사람은 hub 의 정책을 따릅니다.",
    resetLabel: "2단계 해제",
    lockoutHint: "마지막 관리자가 기기를 잃었으면 여기도 못 들어옵니다 — 서버에서 make totp-reset ACCOUNT=<아이디> 를 돌려 주세요.",
  },

  /** 설정 › 로그인 · 2단계. */
  policy: {
    title: "로그인 · 2단계",
    lead: "2단계 인증을 누구에게 강제할지 정합니다. 강제된 사람은 로그인 직후 등록 화면만 열리고, 그 밖의 문은 서버가 막습니다.",
    loadFailed: "정책을 읽지 못했습니다 — {err}. 새로고침해 주세요.",
    loading: "2단계 정책 불러오는 중…",
  },

  /** 설정 › 사용자. 거절(마지막 관리자 · 자기 자신 막기 · 같은 아이디)은 서버 문장 그대로다. */
  users: {
    title: "사용자",
    lead: "이 설치에 들어오는 사람들입니다. 나루 · MCP 세션의 토큰은 이 표에 없습니다 — 「러너」 · 「MCP 토큰」 탭에서 다룹니다.",
    loadFailed: "사용자 목록을 읽지 못했습니다 — {err}. 새로고침해 주세요.",
    loading: "사용자 불러오는 중…",
    noSuch: "그런 사용자가 없습니다.",
    col: {
      door: "들어오는 문",
      totp: "2단계",
      actions: "동작",
      email: "이메일",
      scope: "범위",
    },
    me: "(나)",
    profile: {
      title: "이메일 · 범위",
      email: "이메일",
      emailHint: "hub 로그인이 이 이메일로 계정을 잇습니다. 비우면 지웁니다.",
      scopeNote: "범위를 고르면 그 프로젝트의 이슈만 봅니다. 마일스톤 · 프로젝트 목록은 좁히지 않습니다.",
      save: "이메일 · 범위 저장",
      saved: "저장했습니다",
    },
    door: {
      hubOnly: "hub 만",
      both: "이 설치 + hub 연결",
      local: "이 설치",
    },
    blocked: "막힘",
    fold: "접기 ▴",
    unfold: "펼치기 ▸",
    block: "막기",
    unblock: "막기 풀기",
    selfBlock: "자기 자신은 막을 수 없습니다",
    blockNote: "지우지 않고 막습니다 — 담당 · 이력이 이 이름을 들고 있습니다. 막으면 그 사람의 세션도 끊깁니다.",
    passwordNote: "관리자도 남의 비밀번호를 보거나 정하지 않습니다. 잊었으면 서버에서 {cmd}",
    new: {
      title: "새 계정",
      done: "{name} 을(를) 만들었습니다 — 처음 비밀번호를 본인에게 전해 주세요.",
      password: "처음 비밀번호",
      email: "이메일(선택)",
      note: "처음 비밀번호는 만든 사람이 본인에게 전하고, 본인이 「내 계정」에서 바꿉니다.",
    },
  },

  /** 설정 › 러너(관리자만) — 채택 시안 A안(Figma 15-3). 거절(겹치는 이름 · 설정 파일 러너)은 서버 문장 그대로다. */
  runners: {
    title: "러너",
    lead: "서버마다 붙는 나루입니다. 서버에서 make up S=naru 를 돌려 나온 코드를 여기서 허용하면 토큰이 그 서버로 바로 갑니다. 폐기하면 붙어 있던 연결도 바로 끊깁니다.",
    loadFailed: "러너 목록을 읽지 못했습니다 — {err}. 새로고침해 주세요.",
    loading: "러너 불러오는 중…",
    table: "러너 목록",
    summary: "{n}대 · 붙음 {live}",
    add: "토큰 직접 발급",
    pair: {
      codeLabel: "연결 코드",
      open: "코드로 연결",
      title: "연결 요청 · {code}",
      lead: "서버가 이 설치에 붙겠다고 요청했습니다. 요청한 서버가 맞는지 확인한 뒤 허용해 주세요.",
      askedName: "서버가 댄 이름",
      host: "호스트",
      when: "요청 시각",
      whenText: "{ago} · {left}",
      leftMin: "{n}분 뒤 만료",
      leftSoon: "곧 만료",
      name: "러너 이름",
      nameHint: "영문 소문자 · 숫자 · - · _ 40자까지. 이력과 로그에 이 이름이 남습니다.",
      takenWeb: "「{name}」은 이미 웹에서 발급한 러너입니다. 바꿔서 허용하면 옛 토큰은 폐기되고 붙어 있던 연결이 끊깁니다.",
      approve: "허용",
      replace: "바꿔서 허용",
      deny: "거절",
      close: "닫기",
      loading: "연결 요청 불러오는 중…",
      missing: {
        title: "코드를 찾지 못했습니다",
        desc: "코드가 틀렸거나 10분이 지나 만료됐습니다. 서버에서 make up S=naru 를 다시 실행하면 새 코드가 나옵니다.",
      },
      approved: {
        title: "{name} 을 연결했습니다",
        note: "토큰은 서버로 바로 전달됩니다. 이 화면에는 토큰이 없습니다.",
        desc: "서버가 몇 초 안에 받아 가고 나루를 띄웁니다. 표에서 「붙음」으로 바뀌면 끝입니다.",
      },
      denied: {
        title: "거절했습니다",
        desc: "서버 쪽은 다음 물음에서 거절을 알고 멈춥니다. 토큰은 만들지 않았습니다.",
      },
    },
    col: {
      state: "상태",
      name: "이름",
      tags: "태그",
      vendors: "벤더",
      version: "판",
      seen: "마지막 접속",
      mode: "방식",
      source: "출처",
      action: "동작",
    },
    state: {
      live: "붙음",
      liveMany: "붙음 {n}",
      down: "끊김",
      downFor: "끊김 · {for}",
      never: "안 붙어 봄",
    },
    /** 끊긴 지 얼마나 — 「끊김 · 14분째」. */
    downFor: {
      min: "{n}분째",
      hour: "{n}시간째",
      day: "{n}일째",
    },
    /** hello 가 밝힌 붙은 방식. 옛 나루 · 안 붙은 러너는 「모름」. */
    mode: {
      host: "호스트",
      docker: "도커",
      tip: "호스트 = 서버의 systemd 서비스(make naru-host), 도커 = 컨테이너(make naru-remote)",
    },
    unknown: "모름",
    noTags: "없음",
    source: {
      env: "설정 파일 · 읽기만",
      web: "웹 · {by} · {date}",
    },
    envNote: "설정 파일(api 의 .env B3XIN_AGENT_TOKENS)에 적힌 러너는 여기서 폐기할 수 없습니다.",
    revoke: "폐기",
    revokeConfirm: "한 번 더 눌러 폐기",
    empty: {
      title: "등록된 러너가 없습니다",
      desc: "서버에서 make up S=naru 를 돌리면 연결 코드가 나옵니다. 그 코드를 「코드로 연결」에 넣어 허용해 주세요.",
    },
    new: {
      title: "토큰 직접 발급",
      name: "이름",
      hint: "서버 이름 그대로 적는 것이 좋습니다(예: s114). 나루의 B3XIN_NARU_ID 와 같게 두면 목록에서 찾기 쉽습니다. 태그 · 벤더는 나루가 붙을 때 스스로 밝힙니다.",
      submit: "토큰 발급",
    },
    token: {
      title: "{name} 의 토큰 — 지금 한 번만 보입니다",
      lead: "닫으면 다시 볼 수 없습니다. 잃으면 폐기하고 새로 발급해 주세요.",
      copy: "복사",
      copied: "복사했습니다",
      copyFailed: "복사하지 못했습니다 — 직접 골라 복사해 주세요.",
      where: "그 서버({name})의 나루 폴더 .env 에 B3XIN_AGENT_TOKEN=위 값 으로 적은 뒤 아래를 돌려 주세요. 토큰이 이미 있으면 연결 코드를 묻지 않습니다.",
      done: "다 넣었습니다 · 닫기",
    },
  },

  /** 접근 범위 — MCP 토큰과 계정이 같이 쓴다(components/ProjectScope.tsx). */
  projectScope: {
    title: "범위",
    all: "전체 프로젝트",
    pick: "프로젝트 고르기",
    none: "고른 프로젝트가 없습니다 — 이대로면 이슈를 하나도 못 봅니다.",
    noProjects: "등록된 프로젝트가 없습니다.",
    noneShort: "볼 수 있는 이슈 없음",
    adminAll: "관리자는 늘 전체를 봅니다",
  },

  /** 설정 › MCP 토큰(#/settings?tab=mcp) — 러너 토큰 표의 kind='mcp' 줄. docs/decisions/agent-token.md. */
  mcpTokens: {
    title: "MCP 토큰",
    lead: "Claude Code · Codex 같은 하네스가 b3x-in MCP 에 붙을 때 쓰는 토큰입니다. 링크 승인으로 붙은 클라이언트도 여기 보입니다. 만료가 지나면 바로 끊기고, 범위를 고르면 그 프로젝트의 이슈만 봅니다.",
    loading: "MCP 토큰 불러오는 중…",
    loadFailed: "MCP 토큰 목록을 읽지 못했습니다 — {err}. 새로고침해 주세요.",
    summary: "{n}개 · 만료됨 {expired}",
    add: "토큰 발급",
    createdBy: "{by} · {date}",
    neverUsed: "안 써 봄",
    manage: "관리",
    col: {
      name: "이름",
      created: "발급",
      used: "마지막 사용",
      expires: "만료",
      scope: "범위",
      action: "동작",
    },
    expires: {
      never: "무제한",
      past: "{date} 만료됨",
      days: "{n}일",
    },
    empty: {
      title: "발급한 MCP 토큰이 없습니다",
      desc: "「토큰 발급」으로 만들거나, 하네스에서 b3x-in MCP 를 추가하면 링크 승인으로 붙습니다.",
    },
    form: {
      name: "이름",
      nameHint: "영문 소문자 · 숫자 · - · _ 40자까지. 이슈 이력에 이 이름이 남습니다.",
      expires: "만료",
    },
    new: {
      title: "MCP 토큰 발급",
      submit: "토큰 발급",
    },
    edit: {
      title: "{name} 관리",
      nameHint: "바꾸면 앞으로의 기록이 새 이름으로 남습니다. 지난 이력은 그대로입니다.",
      expiresNow: "지금: {now}",
      keep: "그대로 둠",
      fromToday: "오늘부터 {n}일",
    },
    revokeConfirm: "한 번 더 눌러 폐기",
    revokeNote: "폐기하면 이 토큰을 쓰는 하네스가 바로 끊깁니다. 되돌릴 수 없습니다.",
    token: {
      title: "{name} 의 토큰 — 지금 한 번만 보입니다",
      where: "Claude Code 라면 아래 한 줄을 그대로 돌려 주세요. 다른 하네스는 MCP 주소와 Authorization 머리에 위 토큰을 넣습니다.",
      linkNote: "하네스가 링크 승인을 지원하면 토큰 없이 MCP 주소만 넣어도 됩니다 — 브라우저로 승인하면 여기 토큰이 생깁니다.",
    },
  },

  /** 관리 › 게시판(#/chores) — 사람이 손으로 할 운영 할 일. docs/decisions/chores.md. */
  chores: {
    lead: "에이전트가 남긴, 사람이 손으로 해야 하는 운영 할 일입니다. 끝낸 것은 체크하면 아래로 접힙니다. 에이전트에게 일을 시키려면 이슈를 만들어 주세요.",
    summary: "{open}건 남음 / 전체 {total}건",
    add: "새 항목",
    loading: "게시판을 불러오는 중…",
    loadFailed: "게시판을 불러오지 못했습니다 — {err}",
    openTitle: "남은 일 {n}건",
    openEmpty: "남은 할 일이 없습니다.",
    doneTitle: "끝낸 일 {n}건",
    doneFolded: "접혀 있습니다. 펼치면 끝낸 사람과 시각이 보입니다.",
    doneBy: "{who} 님이 {ago} 끝냄",
    ref: "참고",
    doneAria: "{title} 끝냄으로 표시",
    undoneAria: "{title} 다시 남은 일로",
    deleteConfirm: "한 번 더 누르면 지웁니다",
    author: { agent: "에이전트", human: "사람" },
    form: {
      card: "새 항목",
      title: "할 일",
      titlePh: "예: GitHub 웹훅 설정",
      body: "상세 메모",
      optional: "선택",
      link: "참고 링크 · 문서 경로",
      linkHint: "선택 — http(s) 주소는 링크로, 나머지는 글자 그대로 보입니다.",
      create: "등록",
    },
  },

  /** 관리 › 조직(#/orgs) — docs/decisions/org.md. 역할 이름(OWNER · USER · GUEST)은 원본 그대로 영문이다. */
  orgs: {
    lead: "조직마다 프로젝트 · 멤버 · git 연결을 묶습니다. 조직에 든 프로젝트의 이슈는 그 조직 멤버만 보고, GUEST 는 읽기만 합니다. 조직에 안 든 프로젝트는 누구나 봅니다.",
    summary: "조직 {n}개",
    add: "새 조직",
    loading: "조직 목록을 불러오는 중…",
    loadFailed: "조직 목록을 불러오지 못했습니다 — {err}",
    empty: {
      title: "보이는 조직이 없습니다",
      desc: "속한 조직이 없습니다. 조직에 넣어 달라고 그 조직의 OWNER 나 관리자에게 말해 주세요.",
      adminDesc: "아직 조직이 없습니다. 「새 조직」으로 만들고 프로젝트와 멤버를 넣어 주세요.",
    },
    role: { none: "멤버 아님" },
    guide: {
      title: "역할",
      role: "역할",
      can: "할 수 있는 것",
      cannot: "못 하는 것",
      OWNER: { can: "조직 이름 바꾸기 · 멤버 더하기와 역할 바꾸기 · 조직 프로젝트의 이슈 전부", cannot: "조직 만들기 · 지우기 · 프로젝트 배정 · git 연결 고르기(관리자 몫)" },
      USER: { can: "조직 프로젝트의 이슈 보기 · 만들기 · 고치기", cannot: "조직 설정 · 멤버 관리" },
      GUEST: { can: "조직 프로젝트의 이슈 보기", cannot: "이슈 만들기 · 고치기 · 옮기기 — 쓰기는 전부 막힙니다" },
    },
    card: { projects: "프로젝트", members: "멤버", git: "git 연결", manage: "관리", view: "보기" },
    git: {
      none: "연결 없음",
      hint: "조직마다 하나입니다. 연결 자체는 관리 › git 저장소에서 만듭니다.",
    },
    form: { name: "조직 이름" },
    new: { title: "새 조직", hint: "80자까지 · 대소문자만 다른 이름은 같은 이름으로 봅니다.", submit: "만들기" },
    manage: {
      title: "{name} 관리",
      basic: "기본",
      adminNote: "관리자라서 멤버가 아니어도 전부 엽니다.",
      userNote: "USER 는 조직 설정과 멤버를 바꾸지 못합니다. 바꿀 것이 있으면 OWNER 에게 말해 주세요.",
      guestNote: "GUEST 는 읽기만 합니다. 이 조직 프로젝트의 이슈를 만들거나 고치려면 OWNER 에게 역할을 바꿔 달라고 해 주세요.",
    },
    projects: {
      title: "프로젝트 {n}개",
      lead: "여기 든 프로젝트의 이슈는 이 조직 멤버만 봅니다. 빼면 누구나 보는 프로젝트로 돌아갑니다.",
      empty: "든 프로젝트가 없습니다.",
      noProjects: "등록된 프로젝트가 없습니다. 작업 › 프로젝트에서 먼저 만들어 주세요.",
      inOther: "지금 {org} — 고르면 이리로 옮깁니다",
      save: "프로젝트 저장",
    },
    members: {
      title: "멤버",
      loading: "멤버를 불러오는 중…",
      empty: "멤버가 없습니다",
      emptyDesc: "아이디로 OWNER 를 먼저 세워 주세요. OWNER 가 있어야 관리자 없이도 멤버를 관리합니다.",
      user: "사용자",
      role: "역할",
      action: "동작",
      roleOf: "{user} 역할",
      remove: "빼기",
      removeConfirm: "한 번 더 누르면 뺍니다",
      addUser: "아이디",
      addHint: "설정 › 사용자에 있는 아이디입니다. 이미 멤버면 역할만 바뀝니다.",
      add: "더하기",
    },
    deleteConfirm: "한 번 더 누르면 지웁니다",
    deleteNote: "조직을 지워도 프로젝트 · 이슈 · git 연결은 남고, 누구나 보는 상태로 돌아갑니다.",
  },

  /** 이슈 보드(#/board) · 목록 · 상세 팝업. 끌어 옮기기 거절은 서버 문장 그대로다. */
  servers: {
    lead: "서버마다 붙은 나루가 보고한 서비스 목록입니다. 포트는 각 서비스 폴더 .env 의 *_PORT 값이고, 바로가기를 누르면 새 탭에서 열립니다.",
    rail: "나루(서버)",
    loading: "서버 목록 불러오는 중…",
    loadFailed: "서버 목록을 읽지 못했습니다 — {err}. 새로고침해 주세요.",
    live: "붙음",
    down: "안 붙음",
    reportLine: "{host} · {ip} · {ago} 보고 · 서비스 {n}",
    summary: "{n}대 · 나루 붙음 {live} · 끊김 {down}",
    downNote: "나루 연결이 끊겼습니다(마지막 접속 {ago}). 아래 서비스 상태는 그때 보고라 지금과 다를 수 있습니다. 서버에서 make up S=naru 로 다시 붙여 주세요.",
    table: "서비스 목록",
    none: {
      title: "보고한 서버가 없습니다",
      desc: "서버에서 make up S=naru 로 나루를 붙이고, svc-hosts 의 make report 가 돌면 여기에 뜹니다.",
    },
    noReport: {
      line: "나루는 붙었지만 서비스 목록 보고가 아직 없습니다.",
      title: "서비스 목록을 모릅니다",
      desc: "이 서버의 svc-hosts 가 아직 보고하지 않았습니다. 서버에서 irolab-svc-hosts 를 최신으로 받고(make update) make report 를 실행해 주세요.",
    },
    empty: {
      title: "등록된 서비스가 없습니다",
      desc: "「리포 추가」로 새 리포를 받거나, 이미 클론된 폴더는 서버에서 make append-existing 으로 등록해 주세요.",
    },
    col: {
      state: "상태",
      name: "서비스",
      repo: "리포 · 브랜치",
      ports: "포트 (.env)",
      link: "바로가기",
      dir: "폴더",
    },
    state: {
      running: "도는 중",
      stopped: "멈춤",
      notSynced: "받기 전",
    },
    port: {
      none: "열지 않음",
      unknown: "모름",
      fromList: "세팅 전 — 목록 값",
    },
    link: {
      none: "웹 없음",
      unknown: "웹 포트 모름",
    },
    consoleHint: "이미 서버에 클론된 폴더는 그 서버에서 make append-existing 으로 등록합니다. 등록하면 다음 보고 때 여기에 뜹니다.",
    add: {
      open: "리포 추가",
      title: "{host} 에 새 리포 추가",
      lead: "서버에 아직 없는 리포를 받아(clone) 목록에 등록합니다. 띄우기(make up)는 따로 합니다 — 처음 세팅은 서버에서 사람이 답해야 합니다.",
      repo: "리포",
      repoHint: "이름(irolab-co/이름) 또는 owner/이름. b3x in 에 등록된 리포에서 고를 수 있습니다.",
      repoBad: "이름 또는 owner/이름 모양으로 적어 주세요.",
      already: "이 서버에 이미 있는 리포입니다.",
      branch: "브랜치",
      service: "서비스 이름",
      serviceHint: "비우면 리포 이름입니다.",
      port: "호스트 포트",
      portHint: "비우면 리포 .env.example 의 첫 *_PORT 입니다.",
      portBad: "1–65535 숫자로 적어 주세요.",
      used: "이 서버에서 쓰는 포트: {ports} — 겹치면 서버가 등록을 되돌리고 빈 포트를 알려 줍니다.",
      usedNone: "없음",
      cmd: "서버에서 도는 명령",
      how: "「추가」를 누르면 서버 제어 이슈(대기)가 서고 {host} 가 집어서 돌립니다. 결과는 그 이슈와 이 목록에 남습니다.",
      submit: "추가",
      taskTitle: "{host} 리포 추가: {name}",
      taskIntent: "서버 화면에서 사람이 요청 — {repo} 를 받아(clone) svc-hosts 목록에 등록합니다(make append).",
    },
    jobs: {
      title: "리포 추가 요청",
      open: "이슈 보기",
    },
  },
  board: {
    menu: "이슈 보드 메뉴",
    milestone: "마일스톤",
    all: "전부",
    seeing: "지금 {shown}건 보는 중 · 전체 {total}건",
    loadFailed: "이슈를 읽지 못했습니다 — {err}. 「다시 시도」를 눌러 주세요.",
    loading: "이슈 불러오는 중…",
    missing: "주소가 연 이슈 {id} 가 목록에 없습니다 — 지워졌거나 아직 못 읽었습니다. 알람 화면(#/alarms)에서 그 줄의 제목을 확인해 주세요.",
    viewLabel: "보기 방식",
    viewBoard: "보드",
    viewList: "목록",
    searchHint: "제목 · id · 담당으로 찾기",
    searchLabel: "이슈 찾기",
    msAll: "마일스톤 전부",
    sortLabel: "정렬",
    sort: {
      queue: "줄 순서",
      updated: "최근 바뀐 순",
      priority: "등급 순",
      title: "제목 순",
      created: "등록일 순",
    },
    dir: "정렬 방향",
    asc: "오름차순",
    desc: "내림차순",
    project: "프로젝트",
    projectNone: "미분류",
    projectFailed: "프로젝트 목록을 읽지 못했습니다. 새로고침해 주세요.",
    more: "{n}건 더 보기",
    closed: "완료 · 취소 {n}건",
    pickAll: "보이는 이슈 전부 고르기",
    pickOne: "{title} 고르기",
    fold: "{col} 칸 접기",
    unfold: "{col} 칸 펼치기",
    kidsHere: "이 칸의 하위 {n}",
    progress: "진행률",
    progressVal: "완료 {done} / {total} · {pct}%",
    epicFiltered: "{title} 의 하위만 보는 중입니다. 보드를 좁힌 것이지 다른 화면으로 간 것이 아닙니다.",
    unfilter: "필터 풀기",
    empty: {
      none: "이슈가 아직 없습니다",
      noneDesc: "이 프로젝트에 만들어진 이슈가 없습니다.",
      noMatch: "조건에 맞는 이슈가 없습니다",
      noMatchDesc: "찾는 말이나 마일스톤을 바꿔 주세요. 이슈가 아예 없는 것과는 다릅니다.",
    },
    clear: "조건 지우기",
    colEmpty: "비어 있습니다",
    col: {
      title: "제목",
      priority: "등급",
      complexity: "복잡도",
      updated: "업데이트",
    },
    detail: {
      parent: "상위",
      assignee: "담당",
      held: "세션이 쥠",
      ticket: "사람이 잠가 둠",
      running: "{runner} 에서 세션이 {since} 돌고 있습니다.",
      blockedOpen: "상태는 막힘인데 세션이 아직 안 닫혔습니다 — 상태로 판정하지 않는 이유입니다.",
      intent: "의도",
      notWritten: "아직 안 적혔습니다.",
      acceptanceEmpty: "아직 안 적혔습니다 — 「잘 된다」가 아니라 확인할 수 있는 문장이 기준입니다.",
    },
  },

  /** 이슈 상세의 조작 줄(Figma 28:2). 거절 문장은 여기 없다 — 서버가 준 `message` 를 그대로 보인다. */
  issueActs: {
    label: "이슈 조작",
    move: "단계 옮기기",
    moveCurrent: "{label} · 지금",
    moveNote: "옮길 수 없는 단계면 서버가 이유를 알려 줍니다.",
    handoff: "에이전트에게 넘기기",
    handoffGo: "넘기기",
    unrequest: "자동 실행 끄기",
    toNaru: "나루가 알아서 집게 합니다",
    toNaruBody: "자동 실행 표를 세웁니다. 비어 있는 나루가 다음 판에 집고, 집으면 담당에 그 나루 이름이 뜹니다. 걸려 있던 담당은 먼저 풉니다.",
    toName: "이름을 정해 맡겨 둡니다",
    toNameBody: "그 에이전트(또는 사람) 앞으로 「맡아 둠」을 겁니다. 그 세션이 집으면 담당이 세션 점유로 바뀝니다.",
    namePlaceholder: "이름 (예: naru-2, claude-web)",
    nowAssignee: "지금 담당: {who} · {kind} — 넘기면 바뀝니다.",
    unassign: "담당 해제",
    assignMe: "내가 맡기",
    copyPrompt: "구현 프롬프트 복사",
    copied: "구현 프롬프트를 복사했습니다 — 내 세션(Claude Code 등)에 붙여 넣어 주세요.",
    stop: "세션 멈춤",
    stopArmed: "한 번 더 누르면 멈춥니다",
    cancel: "이슈 취소",
    cancelAsk: "이 이슈를 취소하시겠습니까?",
    cancelBody: "{id} 를 「취소」로 닫습니다. 나중에 「단계 옮기기 → 할 일」로 되살릴 수 있습니다.",
    cancelBodyAssignee: "{id} 를 「취소」로 닫습니다. 걸려 있던 담당({who})도 풀립니다. 나중에 「단계 옮기기 → 할 일」로 되살릴 수 있습니다.",
    cancelGo: "취소하기",
    deleteAsk: "이 이슈를 삭제하시겠습니까?",
    deleteBody: "{id} 를 지웁니다. 되돌릴 수 없습니다.",
    deleteBodyChildren: "{id} 와 하위 {n}건을 함께 지웁니다. 되돌릴 수 없습니다.",
    reviewRerun: "리뷰 다시 돌리기",
    planRun: "기획 배차",
    childrenReady: "하위 전부 대기로",
    childrenMoved: "{n}건 대기로 올렸습니다",
    childrenSkipped: "{n}건 건너뜀({why})",
    childrenDeps: "{n}건은 선행이 끝나야 돕니다",
    skip: {
      alreadyReady: "이미 대기",
      notStartable: "진행 · 검토 중",
      closed: "닫힘",
    },
    blockedNote: "막힘 사유 · {note}",
    blockedNoteNone: "막힘 사유가 적혀 있지 않습니다.",
    why: {
      running: "세션이 돌고 있습니다. 단계 이동 · 담당 해제 · 취소 · 삭제는 「세션 멈춤」 뒤에 할 수 있습니다.",
      parent: "에픽의 단계는 하위가 정합니다 — 취소하려면 하위를 취소해 주세요.",
      heldDelete: "세션이 쥐고 있어 지울 수 없습니다. 먼저 「담당 해제」를 눌러 주세요.",
    },
  },

  /** 이슈 상세의 고치는 자리 — 스펙 편집 · 열린 질문 · 막힘 보완 · 선행 · 하위(Figma 31:2). */
  issueEdit: {
    label: "스펙 편집",
    edit: "편집",
    title: "제목",
    type: "유형",
    priority: "우선순위",
    complexity: "복잡도",
    acceptance: "수용 기준 — 한 줄에 하나",
    questions: "열린 질문 {n}",
    answer: "답하기",
    answerPlaceholder: "직접 적어 답하기",
    supplement: "막힘 보완 — 다음 세션에 남길 말",
    supplementBody: "적은 말은 이슈 우편함에 남고, 이슈는 대기로 올라가 나루가 다시 집습니다.",
    supplementPlaceholder: "무엇이 빠졌는지 · 어떻게 풀지 적어 주세요",
    supplementGo: "보완하고 다시 돌리기",
    deps: "선행 {n}",
    depRemove: "선행 {id} 풀기",
    depPlaceholder: "선행 이슈 id",
    depAdd: "선행 추가",
    children: "하위 {n}",
    childPlaceholder: "새 하위 이슈 제목",
    childAdd: "하위 추가",
  },

  /** 이슈 상세의 탭(Figma 33:2) — b3x-pr 상세의 탭 구성. 「맥락」은 b3x-in 에 그 칸이 없어 뺐다. */
  issueTabs: {
    label: "이슈 상세",
    intent: "의도",
    acceptance: "완료 기준",
    info: "정보",
    handoff: "인계",
    history: "이력",
    handoffNone: "아직 인계 노트가 없습니다 — 세션이 제출할 때 남깁니다.",
    next: "후속 {n}",
    nextNone: "이 이슈를 선행으로 건 이슈가 없습니다.",
    attachments: "첨부 {n}",
    attachmentsNone: "첨부가 없습니다.",
    attSize: "{kb}KB · {who}",
    createdAt: "만든 때",
    updatedAt: "고친 때",
    unknown: "모름",
    historyLoading: "이동 기록을 불러오는 중…",
    historyNone: "아직 이동 기록이 없습니다.",
    historyFail: "이동 기록을 불러오지 못했습니다 — {err}",
  },

  /** 이슈 상세의 틀 — 모달 머리(뒤로 · 앞으로 · 전체 화면) · 메타 줄 · 바닥. 보드 · 목록 · 전체 화면이 같이 쓴다. */
  issueDetail: {
    project: "프로젝트",
    agent: "에이전트",
    meta: "이슈 정보 요약",
    back: "앞서 본 이슈로",
    forward: "다음 이슈로",
    full: "전체 화면으로 열기",
    copyLink: "주소 복사",
    copied: "복사했습니다",
    copyFailed: "복사하지 못했습니다",
    page: "이슈 상세",
    notFound: "이슈 {id} 를 찾지 못했습니다. 지워졌거나 볼 수 없는 이슈입니다.",
    loadFailed: "이슈를 불러오지 못했습니다 — {err}",
    toBoard: "보드로",
  },

  /** 보드의 선택 줄 — 카드를 골라 한꺼번에(Figma 31:2 ③). */
  boardSel: {
    pick: "{title} 고르기",
    count: "{n}건 선택됨",
    toReady: "대기로",
    toNaru: "나루에게 넘기기",
    unrequest: "넘기기 해제",
    clear: "선택 해제",
    deleteAsk: "고른 {n}건을 삭제하시겠습니까? 되돌릴 수 없습니다.",
    deleteGo: "삭제하기",
    result: "{ok}건 됐습니다",
    resultFail: "{ok}건 됐고 {fail}건은 못 했습니다 — {why}",
  },

  /** 채팅 겉창 · 세션 탭 · 단계 띠 · 대화 줄. 로그 줄의 말/진행은 서버가 접은 값이고 여기는 이름표뿐이다. */
  chat: {
    label: "세션 채팅",
    issue: "이슈",
    noIssue: "이슈 없음",
    pickIssue: "이슈 고르기 — 도는 것 먼저, 그다음 최근에 세션이 돌았던 이슈",
    runningUnknown: "도는 세션 모름",
    reload: "목록 다시 읽기",
    close: "채팅 겉창 닫기",
    closeEsc: "닫기 (Esc)",
    listFailed: "목록을 읽지 못했습니다 — {err}. API 가 떠 있는지 확인해 주세요.",
    logFailed: "로그를 읽지 못했습니다 — {err}. 잠시 뒤 다시 열어 주세요.",
    eventsFailed: "이력을 읽지 못했습니다 — {err}. 잠시 뒤 다시 열어 주세요.",
    sendFailed: "말을 남기지 못했습니다 — {err}. 다시 보내 주세요.",
    stopFailed: "멈추지 못했습니다 — {err}. 다시 눌러 주세요.",
    empty: {
      title: "이슈도 세션도 없습니다",
      desc: "아직 이슈가 하나도 없습니다. 서버에서 make chat-sample 을 돌리면 이슈 둘이 생기고 나루가 집습니다(make up-naru 가 떠 있어야 합니다). Roo Code 로 붙어서 일해도 여기 남습니다.",
    },
    noLog: {
      title: "로그가 없습니다",
      desc: "세션은 있는데 줄이 0 입니다 — 방금 열렸거나(프롬프트도 아직), 나루가 밀기에 실패하고 있습니다(나루 로그를 확인해 주세요).",
    },
    noFootprint: {
      title: "발자국이 없습니다",
      desc: "아직 이 이슈를 아무도 안 건드렸습니다. 상태를 옮기거나 MCP 로 링크를 남기면 여기 남습니다.",
    },
    footprintLead:
      "사람 세션(Roo Code)은 세션 행이 없습니다 — 이 이슈의 발자국이 곧 그 대화입니다. 상태 이동 · 링크 · 우편함 · 세션 열고 닫음이 시간순으로 섭니다.",
    lines: "{n}줄",
    tokens: "토큰",
    unknown: "모름",
    more: "↑ 앞 줄 더 보기 (전체 {total}줄 중 뒤 {shown}줄)",
    hint: {
      stopping: "멈춤을 요청했습니다 — 나루가 다음 박동에 끊고 이슈는 「할 일」로 돌아갑니다.",
      running: "도는 세션이라 말을 걸 수 있습니다. 멈추면 이슈는 「할 일」로 돌아갑니다.",
      ended: "끝난 세션이라 입력칸이 없습니다 — 말을 남기려면 「발자국」 탭에서 남겨 주세요(다음에 집는 세션이 받습니다).",
      closed: "닫힌 이슈입니다 — 말을 남겨도 집어 갈 세션이 없습니다.",
      noSession: "열린 세션이 없으면 다음에 집는 세션(Roo Code 포함)이 받습니다. 멈출 세션은 없습니다.",
    },
    input: {
      hint: "세션에 말 걸기 — 경계마다 집어 갑니다",
      label: "세션에 남길 말",
      send: "보내기",
      stoppingTip: "멈춤을 요청했습니다 — 나루가 다음 박동에 끊습니다",
      stopTip: "세션을 멈춥니다 — 이슈는 할 일로 돌아갑니다",
      arm: "한 번 더 눌러 멈추기",
    },
    stop: "멈춤",
    stopping: "멈추는 중",
    ended: "끝남",
    endUnknown: "끝난 이유 모름",
    sessions: "세션",
    footprint: "발자국",
    footprintTip: "세션 행이 없는 사람 세션(Roo Code)의 자리 — 이 이슈의 발자국이 곧 그 대화입니다",
    noStageSession: "이 단계엔 세션이 없습니다",
    sessionEnd: "{track} 세션의 끝",
    /** 트랙이 없는 세션을 한 칸으로 모으는 이름 — 단계 띠의 키로도 쓰인다. */
    noTrack: "(트랙 없음)",
    trackUnknown: "트랙 모름",
    track: {
      plan: "기획",
      design: "설계",
      run: "구현",
      review: "리뷰",
      merge: "머지",
    },
    stage: {
      label: "단계",
      notYet: "아직",
      times: "{n}회",
      sessionTimes: "세션 {n}회",
      none: "세션 없음",
    },
    chars: "{n}자",
    promptSummary: "{chars} · 눌러서 펼치기",
    kind: {
      prompt: "프롬프트",
      message: "말",
      system: "시스템",
      warning: "경고",
      activity: "도구",
      thinking: "생각",
      toolResult: "결과",
      stderr: "표준오류",
      raw: "원문",
    },
    event: {
      link: "링크",
      message: "말 남김",
      receive: "우편함",
      sessionOpen: "세션 시작",
      sessionClose: "세션 끝",
    },
    delivery: {
      read: "세션이 읽음",
      lost: "전달 못 함 — 그 세션이 집기 전에 끝났습니다",
      waitSession: "전달 대기 — 세션이 다음 경계에서 집어 갑니다",
      waitNext: "전달 대기 — 열린 세션이 없어 다음에 집는 세션이 받습니다",
    },
    time: {
      unknown: "시각 모름",
      minAgo: "{n}분 전",
      hourAgo: "{n}시간 전",
      dayAgo: "{n}일 전",
      startUnknown: "시작 모름",
      justStarted: "방금 시작",
      durUnknown: "걸린 시간 모름",
      sec: "{n}초",
      hourMin: "{h}시간 {m}분",
    },
  },

  /** 알람 — 종 · 종 팝오버 · 알람 화면(#/alarms). 줄의 본문(body)은 서버가 만든 문장이라 그대로 보인다. */
  alarm: {
    title: "알람",
    bellTip: "알람 — 막힘 · 재작업 · 세션 실패 · 멈춘 진행 · 검토 대기",
    countFailedLabel: "알람 · 안 읽은 수를 읽지 못했습니다",
    countFailedTip: "안 읽은 수를 읽지 못했습니다 — 화면을 옮기면 다시 읽습니다",
    countUnknown: "안 읽은 수 모름",
    unread: "안 읽음",
    unreadN: "안 읽음 {n}",
    unreadAlert: "안 읽음 {n} · 그중 주의 {alert}",
    readAll: "모두 읽음",
    seeAll: "전체 보기 → 알람 화면",
    recentOf: "{total}건 중 최근 {n}건",
    loadFailed: "알람을 읽지 못했습니다 — {err}. api 가 떠 있는지 `make dev-status` 로 확인해 주세요.",
    loading: "알람 불러오는 중…",
    empty: {
      filtered: "이 거르개에 맞는 알람이 없습니다",
      filteredDesc: "「안 읽은 것만」이나 갈래 거르개를 풀면 지난 알람이 보입니다.",
      none: "볼 알람이 없습니다",
      noneDesc: "막힘 · 재작업 · 세션 실패 · 멈춘 진행 · 검토 대기 · 러너 끊김이 생기면 여기 뜹니다.",
    },
    kind: {
      blocked: "막힘",
      rework: "재작업",
      sessionFailed: "세션 실패",
      stale: "멈춘 진행",
      review: "검토 대기",
      runnerDown: "러너 끊김",
    },
    /** 끝난 끊김(다시 붙음) — 줄은 남고 흐려진다. */
    resolved: "다시 붙음 · {ago}",
    /** 「누르면 →」 한 마디. */
    go: {
      servers: "서버 화면의 그 러너",
      session: "채팅 겉창의 그 세션",
      task: "이슈 상세",
      none: "이슈 없음 — 이 화면",
    },
    table: {
      pickAll: "보이는 알람 전부 고르기",
      pick: "{title} 알람 고르기",
      kind: "갈래",
      body: "내용",
      when: "언제",
      gone: "이슈가 지워졌습니다 — 이 줄이 그때의 제목을 들고 있습니다",
      picked: "{n}건 선택됨",
      clear: "선택 해제",
      markRead: "읽음으로",
    },
    page: {
      menu: "알람 메뉴",
      view: "보기",
      onlyUnread: "안 읽은 것만",
      count: "{n}건",
      partial: "{total}건 중 {n}건 — 더 있습니다 ·",
      end: "{n}건 — 끝",
      loadMore: "더 보기",
      range: "기간",
      rangeOf: { today: "오늘", "7d": "7일", "30d": "30일", all: "전체" },
      task: "대상 이슈 id",
      taskPh: "이슈 id — Enter 로 거릅니다",
      reload: "다시 불러오기",
      hint: "줄을 누르면 대상 이슈로 가면서 읽음이 됩니다. 왼쪽 칸을 골라 여러 개를 한 번에 읽음으로 바꿀 수 있습니다.",
    },
  },

  /** 이슈 정리(#/tidy). 건너뜀 사유는 서버 `message` 가 있으면 그것이 먼저다 — 여기는 코드만 올 때의 문장. */
  tidy: {
    lead: "세션이 남긴 정리 제안을 골라 적용합니다 — 제안은 에이전트만, 적용은 사람만 합니다.",
    request: "정리 요청",
    loadFailed: "정리 제안을 읽지 못했습니다 — {err}. api 가 떠 있는지 `make dev-status` 로 확인해 주세요.",
    reload: "다시 읽기",
    loading: "제안 불러오는 중…",
    dismiss: "기각",
    dismissTip: "이슈는 안 건드리고 이 제안 줄만 접습니다 — 되돌릴 수 없습니다",
    project: "프로젝트",
    allProjects: "전체 프로젝트",
    noProject: "미분류",
    copyPrompt: "정리 배차 프롬프트 복사",
    copyPromptNoAnchor: "정리 요청을 먼저 눌러 주세요 — 앵커 이슈가 생겨야 프롬프트를 만들 수 있습니다.",
    copied: "정리 프롬프트를 복사했습니다 — 내 세션에 붙여 넣어 주세요. 나루도 같은 앵커를 집을 수 있으니 한쪽만 돌려 주세요.",
    reasonMore: "근거 펼치기",
    reasonLess: "접기",
    legend: "적용은 건별로 부릅니다 — 한 건이 건너뛰어져도 나머지는 갑니다. 적용하면:",
    empty: {
      never: "아직 정리를 돌린 적이 없습니다",
      neverDesc: "마지막 정리: 기록 없음 — 0건이 아니라 안 훑은 것입니다. 정리 요청을 누르면 나루가 이 설치의 열린 이슈를 훑고 제안만 남깁니다.",
      noOpen: "열린 정리 제안이 없습니다",
      noMatch: "이 거르개에 맞는 제안이 없습니다",
      desc: "지난 정리에서 걸린 것이 없었거나 전부 처리됐습니다. 적용됨 · 기각됨은 왼쪽 「상태」에서 볼 수 있습니다.",
    },
    scope: {
      label: "훑는 범위",
      where: "이 설치 전체",
      open: "열린 이슈 {n}",
      last: "마지막 정리 {when}",
      never: "기록 없음",
      requested: "요청됨 · 나루 대기",
      note: "나루가 훑어 제안만 남깁니다. 이슈는 하나도 안 움직입니다.",
    },
    result: {
      applied: "적용 {n}건",
      skipped: "건너뜀 {n}건",
      note: "건너뛴 제안은 목록에 그대로 남습니다 — 나중에 다시 누를 수 있습니다.",
    },
    table: {
      pickAll: "보이는 열린 제안 전부 고르기",
      pick: "{title} 고르기",
      issue: "이슈 — 무엇을",
      kind: "갈래 — 어떻게",
      why: "근거 — 왜",
      reason: "근거",
      when: "제안",
      noTitle: "제목 모름 — 목록에 없는 이슈",
      keep: "남길 쪽",
      designRef: "선행으로 걸 시안",
      notListed: "(목록에 없음)",
    },
    bar: {
      picked: "{n}건 고름",
      none: "고른 것 없음",
      apply: "적용 — 이슈 옮기기(사람만)",
      confirm: "{n}건을 적용하면 이슈가 실제로 움직입니다 — 되돌리려면 이슈마다 손으로 옮겨야 합니다.",
      back: "돌아가기",
      go: "적용",
    },
    kind: {
      duplicate: "중복",
      alreadyDone: "이미 끝남",
      designFirst: "시안 먼저",
    },
    /** 「적용하면 →」 — 갈래마다 적용이 무엇을 하는지. */
    applies: {
      duplicate: "이 이슈를 취소하고 남길 쪽을 이력에 적습니다",
      alreadyDone: "완료로 옮깁니다(검토에서만 닿습니다)",
      stale: "대기로 되돌립니다 — 배차가 다시 집습니다",
      designFirst: "시안 이슈를 선행으로 겁니다",
    },
    status: {
      open: "열림",
      applied: "적용됨",
      dismissed: "기각됨",
    },
    /** 적용이 건너뛴 사유 — 서버가 코드만 줄 때의 문장(서버 `message` 가 있으면 그것을 보인다). */
    skip: {
      held: "누가 쥐고 있습니다 — 열린 세션이나 사람이 건 담당이 끝난 뒤 다시 눌러 주세요.",
      invalidTransition: "지금 단계에서 그리로 갈 수 없습니다 — 완료는 검토에서만 닿습니다. 다른 이슈에서 끝냈다면 「중복」 갈래가 맞습니다.",
      parentDerived: "하위가 있는 이슈라 상태가 하위에서 정해집니다 — 하위를 정리해 주세요.",
      depCycle: "선행을 걸면 고리가 생깁니다 — 시안 이슈가 이미 이 이슈를 기다립니다.",
      depNotFound: "선행으로 걸 이슈가 없습니다.",
      notStale: "그 사이 누가 단계를 옮겼습니다 — 멈춘 진행이 아닙니다.",
      taskNotFound: "제안이 가리키는 이슈가 지워졌습니다. 이 제안은 기각해 주세요.",
      refNotFound: "함께 든 이슈가 지워졌습니다. 이 제안은 기각해 주세요.",
      depsNotDone: "선행이 아직 안 끝나 그 단계로 갈 수 없습니다.",
    },
  },

  /** 도움말(#/help) — 연결 방식 둘 · 화면별 절. 화면 절은 그 화면의 리드를 다시 쓰고, 리드가 없는 화면만 여기 둔다. */
  help: {
    lead: "에이전트를 b3x-in 에 붙이는 방법과 화면마다의 설명을 모았습니다. 화면 제목 옆 (?) 에서 이 페이지의 해당 절로 옵니다.",
    connect: "에이전트 붙이기",
    tabs: "연결 방식",
    tabB: "방식 B · MCP 연결",
    tabA: "방식 A · 복붙",
    recommended: "추천",
    b: {
      lead: "에이전트가 이슈를 직접 읽고 쓰는 방식입니다. 토큰을 손으로 넣지 않습니다 — 주소만 넣으면 링크 승인으로 붙습니다.",
      step1: "에이전트 설정에 MCP 주소를 넣습니다.",
      step2: "에이전트가 브라우저로 승인 링크를 엽니다. 관리자가 누가(클라이언트 이름) · 어디로 받는지 보고 허용합니다.",
      step3: "허용하면 토큰이 알아서 에이전트로 들어갑니다. 그 토큰은 설정 › MCP 토큰에 보이고 거기서 폐기합니다.",
      claude: "Claude Code 라면 이 한 줄입니다.",
      https: "https 주소에 사설 인증서를 쓰면 에이전트 기계에 루트 인증서(/ca.crt)를 먼저 등록해 주세요.",
    },
    a: {
      lead: "MCP 를 못 붙이는 자리에서 쓰는 방식입니다.",
      step1: "이슈 상세의 조작 줄에서 「구현 프롬프트 복사」를 누릅니다.",
      step2: "내 세션(Claude Code 등)에 붙여 넣습니다.",
      step3: "세션이 낸 결과(PR 링크 등)는 사람이 이슈에 남깁니다 — 이 방식의 세션은 b3x-in 에 직접 쓰지 못합니다.",
    },
    toc: "화면별 도움말",
    open: "화면 열기",
    more: "도움말에서 더 보기",
    sec: {
      board: "이슈를 단계별 칸(할 일 · 대기 · 진행 · 검토 · 완료)이나 목록으로 봅니다. 카드를 끌어 단계와 줄 순서를 바꾸고, 골라서 한꺼번에 나루에게 넘깁니다.",
      alarms: "막힘 · 질문 · 러너 끊김처럼 사람이 볼 일을 모읍니다. 숫자는 상단의 종이 듭니다.",
      logs: "api · mcp · 나루의 서버 기록을 거르고 읽습니다. 관리자만 엽니다.",
      settings: "로그인 · 2단계 인증 · 배차 벤더 · 러너 같은 설치 설정입니다.",
      conn: "모델 서버(ola-mops)와 적재(dbix) 연결을 정합니다. 관리자만 엽니다.",
      git: "Gitea · GitHub 연결과 리포 등록입니다. 관리자만 엽니다.",
      code: "mops-code 에 등록된 저장소 분석으로 갑니다. 관리자만 엽니다.",
    },
  },

  /** 일감 › 그래프(#/graph) — 선행 · 상하위 노드-선 그림. */
  graph: {
    lead: "이슈 사이의 선행(실선 화살표)과 상하위(점선)를 봅니다. 노드를 누르면 상세가 열립니다.",
    foldKids: "하위 접기",
    hideDone: "완료 숨기기",
    find: "찾기",
    findHint: "id 나 제목",
    notFound: "{q} 에 맞는 이슈가 지금 고른 프로젝트에 없습니다. 프로젝트를 바꿔 주세요.",
    zoomIn: "확대",
    zoomOut: "축소",
    fit: "맞춤",
    facts: {
      nodes: "노드 {n}",
      edges: "선 {n}",
      folded: "하위 접힘 {n}",
      hidden: "완료 숨김 {n}",
    },
    legend: "실선 화살표 = 선행 · 점선 = 하위",
    kids: "하위 {done}/{n} 완료",
    preds: "선행 {n}",
    predsTip: "이 이슈가 기다리는 선행 수",
    expand: "{title} 하위 펼치기",
    collapse: "{title} 하위 접기",
    empty: "그릴 이슈가 없습니다",
    emptyDesc: "이 프로젝트에 이슈가 없습니다.",
    emptyHidden: "전부 완료라 숨겼습니다. 「완료 숨기기」를 꺼 주세요.",
    loadFailed: "이슈를 읽지 못했습니다 — {err}. 새로고침해 주세요.",
  },

  /** 일감 › 대기열(#/queue) — 나루가 다음에 집을 순서. 사유 코드의 문장은 여기, 판정은 서버. */
  queue: {
    lead: "자동 실행을 켠 이슈가 나루가 집을 순서대로 섭니다. 위아래로 옮기면 줄 순서가 바뀝니다.",
    count: "{n}건 · 기다리는 중 {w}건",
    rule: "순서는 마일스톤 → 줄 순서 → 등록순입니다. 등급은 처음 설 자리만 정합니다 — 맨 위로 올려도 그 마일스톤 차례가 아니면 돌지 않습니다.",
    loadFailed: "대기열을 읽지 못했습니다 — {err}. 「새로고침」을 눌러 주세요.",
    empty: "대기열이 비어 있습니다",
    emptyDesc: "자동 실행을 켠 이슈가 없습니다. 보드에서 이슈를 고르고 「나루에게 넘기기」를 눌러 주세요.",
    col: {
      repos: "리포",
      wait: "기다림",
      state: "상태",
      move: "옮기기",
    },
    days: "{n}일",
    next: "다음 차례 · {track}",
    take: "{track} 가능",
    top: "맨 위로",
    upLabel: "{title} 위로",
    downLabel: "{title} 아래로",
    skip: {
      "has-children": "에픽",
      "session-open": "도는 중",
      held: "잡혀 있음",
      deps: "선행 대기",
      stalled: "멈춘 진행",
      "no-track": "배차 대상 아님",
      "needs-tag": "맞는 나루 없음",
    },
    why: {
      toggle: "왜 안 가나",
      "has-children": "하위가 있는 이슈는 직접 돌지 않습니다. 도는 것은 그 하위입니다.",
      "session-open": "이미 세션이 돌고 있습니다. 끝나면 다음 단계로 갑니다.",
      held: "사람이 맡아 뒀거나 다른 세션이 쥐고 있습니다. 담당을 풀면 줄에 돌아옵니다.",
      deps: "선행 이슈가 아직 안 끝났습니다. 선행이 닫히면 돕니다.",
      stalled: "세션은 끝났는데 이슈가 안 움직였습니다. 이슈를 열어 다시 돌릴지 정해 주세요.",
      "no-track": "지금 단계(할 일 · 막힘)는 배차 대상이 아닙니다. 대기로 올리거나 막힘을 풀어 주세요.",
      "needs-tag": "이 일이 요구하는 능력을 가진 나루가 없습니다. 태그가 맞는 나루를 붙여 주세요.",
    },
  },

  /** 관측 › 이력(#/history) — 끝난 이슈마다 토큰 · 소요. */
  hist: {
    lead: "끝난 이슈마다 토큰과 소요를 돌아봅니다. 소요는 그 이슈 세션들이 돈 시간의 합입니다. 세션이 없거나 토큰을 못 읽은 이슈는 「모름」이고 정렬에서 늘 뒤로 갑니다. 취소한 이슈는 세지 않습니다.",
    count: "{n}건 · 사용량 기록 {m}건",
    range: { all: "전체", "1d": "24시간", "7d": "7일", "30d": "30일", "90d": "90일" },
    project: "프로젝트",
    projectAll: "프로젝트 전체",
    projectNone: "미분류",
    sort: "정렬 기준",
    sortBy: { doneAt: "완료 시각", tokens: "토큰", ms: "소요", createdAt: "등록일", title: "제목" },
    dir: "정렬 방향",
    asc: "오름차순",
    desc: "내림차순",
    view: "보기",
    sum: "합계",
    avg: "이슈당 평균",
    stat: {
      sum: { tokens: "토큰 합계", ms: "소요 합계", sessions: "세션 합계" },
      avg: { tokens: "이슈당 토큰", ms: "이슈당 소요", sessions: "이슈당 세션" },
    },
    col: { project: "프로젝트", doneAt: "완료 시각" },
    noTime: "시각 모름",
    capped: "최근 세션 1,000건만 셌습니다 — 오래 전에 끝난 이슈의 토큰 · 소요는 「모름」으로 보일 수 있습니다.",
    empty: "맞는 끝난 이슈가 없습니다",
    emptyDesc: "기간을 넓히거나 프로젝트 거르개를 비워 주세요.",
    loadFailed: "이력을 불러오지 못했습니다 — {err}. 새로고침해 주세요.",
  },

  /** 세션 진행(#/session) — 관측 › 지금(t-in-port-obs-ops). 판 다섯: 사용량 · 잠긴 이슈 · 나루 · 세션 · 이벤트. */
  ops: {
    lead: "지금 무엇이 돌고, 얼마나 쓰고, 무엇이 걸려 있는지 봅니다. 토큰 수는 세션이 밝힌 값이고, 못 읽은 값은 「모름」입니다. 비용과 예산은 온라인 애드온 판정이라 여기에 없습니다.",
    range: "기간",
    ranges: { "1d": "오늘 24시간", "7d": "최근 7일", "30d": "최근 30일" },
    reload: "다시 불러오기",
    toBoard: "보드 보기",
    unknown: "모름",
    none: "없음",
    count: "{n}건",
    more: "더 보기 ({n}건 남음)",
    task: "이슈 id 로 좁히기",
    taskPh: "이슈 id",
    loadFailed: "{what}을 불러오지 못했습니다 — {err}. 다시 불러오기를 눌러 주세요.",
    col: { issue: "이슈", status: "단계", action: "동작" },
    dur: { sec: "{n}초", min: "{n}분", hour: "{h}시간 {m}분" },
    usage: {
      title: "토큰 사용량",
      help: "기간 안에 시작한 세션의 토큰을 묶음별로 더합니다. 토큰을 못 읽은 세션은 「계측됨」에서 빠집니다.",
      axis: "묶음",
      by: { track: "단계", complexity: "복잡도", model: "모델", vendor: "벤더", runner: "나루", project: "프로젝트" },
      stat: { input: "입력 토큰", output: "출력 토큰", sessions: "세션 · 계측됨", dur: "소요" },
      measuredOf: "{m} / {n}",
      col: { sessions: "세션", measured: "계측됨" },
      capped: "최근 세션 1,000건만 셌습니다 — 이 기간에 더 있을 수 있습니다. 기간을 줄여 주세요.",
      empty: "이 기간에 시작한 세션이 없습니다",
      emptyDesc: "기간을 넓히거나, 이슈를 나루에게 넘기면 여기 쌓입니다.",
    },
    locked: {
      title: "잠긴 이슈",
      help: "담당이 박혀 있는데 도는 세션이 없는 열린 이슈입니다. 나루는 담당이 있는 이슈를 집지 않습니다 — 풀어야 다시 돕니다.",
      col: { assignee: "담당", kind: "잠금" },
      held: "세션이 쥠",
      ticket: "잠가 둠",
      unlock: "담당 풀기",
      empty: "잠긴 이슈가 없습니다",
      emptyDesc: "담당이 남은 채 멈춘 이슈가 생기면 여기 뜹니다.",
    },
    naru: {
      title: "나루",
      help: "이 설치에 등록된 러너입니다. 토큰 발급 · 폐기는 설정 › 러너에서 합니다.",
      adminOnly: "러너 목록은 관리자만 봅니다.",
      liveOnly: "도는 것만",
      manage: "설정 › 러너",
      col: { name: "이름", state: "상태", mode: "방식", vendors: "벤더", version: "판", first: "처음 등록", seen: "마지막 접속" },
      empty: "등록된 러너가 없습니다",
      emptyLive: "지금 붙은 러너가 없습니다",
      emptyDesc: "설정 › 러너에서 연결 코드를 받아 나루를 붙여 주세요.",
    },
    sess: {
      title: "세션 이력",
      help: "도는 세션이 먼저, 그다음 최근 순입니다. 로그는 채팅 겉창에서 엽니다.",
      col: { track: "트랙", naru: "나루", start: "시작", tokens: "토큰", end: "끝", log: "로그" },
      running: "도는 중",
      growing: "계속 자람",
      open: "로그",
      empty: "세션 기록이 없습니다",
      emptyDesc: "이슈 id 를 비우거나, 이슈를 나루에게 넘기면 여기 남습니다.",
    },
    ana: {
      legend: "단계",
      chart: "단계별 토큰 누적 막대",
      stagesTitle: "이슈마다 단계별로",
      stagesHelp: "기간 안 세션을 이슈 × 단계로 더합니다. 「—」는 그 단계 세션이 없음, 「모름」은 세션은 있는데 토큰을 못 읽음입니다.",
      cellTip: "세션 {n}건",
      total: "합계",
      saveTitle: "무엇을 줄일까",
      saveHelp: "판정이 아니라 사실만 꺼냅니다 — 어디를 먼저 열어 볼지 가리킵니다.",
      hint: {
        failed: "끝내지 못한 세션 {n}건이 토큰 {tok}을 썼습니다. 막힌 이슈의 원인을 먼저 풀면 같은 실패를 다시 사지 않습니다.",
        failedUnknown: "끝내지 못한 세션이 {n}건입니다(쓴 토큰은 모름). 막힌 이슈의 원인을 먼저 풀면 같은 실패를 다시 사지 않습니다.",
        unmeasured: "토큰을 못 읽은 세션이 {n}건입니다 — 그 벤더는 사용량을 안 밝히므로 이 표의 합보다 실제로 더 썼습니다.",
        top: "「{title}」 한 이슈가 계측된 토큰의 {pct}%를 썼습니다.",
      },
      open: "이슈 열기",
      hintNone: "눈에 띄는 것이 없습니다",
      hintNoneDesc: "끝내지 못한 세션 · 못 읽은 세션이 없고, 한 이슈에 몰리지도 않았습니다.",
    },
    ev: {
      title: "이벤트 조회",
      help: "이슈 상태 변화와 발자국(링크 · 우편함 · 세션 열고 닫음)을 최근 순으로 봅니다. 최근 500건까지입니다.",
      kind: "종류",
      kindAll: "종류 전체",
      status: "상태 변경",
      col: { at: "시각", msg: "메시지", actor: "누가" },
      empty: "맞는 이벤트가 없습니다",
      emptyDesc: "종류나 이슈 id 를 비워 주세요.",
    },
  },

  /** 설정(#/settings) — 배차 벤더. 사용자 · 로그인 정책은 users · policy 에 있다. */
  settings: {
    menu: "설정 메뉴",
    vendors: {
      title: "배차 벤더",
      lead: "단계마다 어떤 에이전트가 맡을지 여기서 고릅니다. 재배포 없이 바로 반영됩니다. 고를 수 있는 이름은 {plugged}만 뜹니다 — 폐쇄망 빌드에는 claude·codex 가 없습니다.",
      plugged: "이 빌드에 꽂힌 것",
      envNote: "여기서 지운 자리는 설치할 때 {env} 에 적어 둔 값으로 돌아갑니다 — 아무 층도 안 고르면 지금까지와 똑같이 돕니다.",
      readOnly: "보기만 할 수 있습니다 — 바꾸는 것은 관리자만 합니다.",
      loadFailed: "저장된 값을 읽지 못했습니다 — {err}. 새로고침해 주세요.",
      table: "단계별 벤더",
      track: "트랙",
      vendor: "벤더",
      unset: "(안 고름 — env 기본값)",
      loading: "벤더 기본값 불러오는 중…",
      noFallback: "고른 벤더가 그 트랙을 못 맡으면 — {no} 이유를 말하며 그 이슈만 멈춥니다(대신 돌면 화면에서 고른 것이 도는 줄 알게 되고, 그 착각은 결과물을 봐도 안 드러납니다).",
      noFallbackBold: "다른 벤더로 대신 돌리지 않습니다.",
      saveFailed: "저장 실패 — {err}",
      unsaved: "저장 안 한 것이 있습니다",
      changed: "{track} 바뀜",
      revert: "되돌리기",
    },
  },

  /** 연결 설정(#/conn) — ola-mops 주소 · 토큰 · 모델. 결과 배너는 화면이 시험 결과의 갈래로 고른다. */
  conn: {
    lead1: "ola-mops 의 주소와 토큰을 넣습니다. {b} 지금 칸에 적힌 값으로 돕니다.",
    lead1Bold: "연결 시험은 저장 없이",
    lead2: "b3x-in 이 ola-mops 에 어떻게 붙어 있는지 정합니다 — 24GB 서버가 들어오는 날 바꾸는 것도 이 화면입니다. 모델 목록은 {b}만 옵니다 — 고를 것이 없으면 토큰 쪽 설정이지 이 화면의 문제가 아닙니다.",
    lead2Bold: "토큰이 쓸 수 있는 것",
    loadFailed: "저장된 설정을 읽지 못했습니다 — {err}. 새로고침해 주세요.",
    address: "서버 주소",
    addressHint: "ola-mops 의 입구입니다. 뒤에 /v1 은 붙이지 말아 주세요 — 화면이 붙입니다.",
    addressChanged: "주소 바뀜",
    token: "API 토큰",
    tokenHintSet: "저장돼 있습니다. 화면으로 되돌려 주지 않아 값은 안 보입니다 — 바꾸려면 새 값을 적어 주세요.",
    tokenHintNone: "아직 없습니다. ola-mops 관리자 화면에서 발급한 값을 적어 주세요.",
    tokenPhSet: "●●●●●●●●●●●●  (바꾸려면 새 값)",
    tokenPhNone: "토큰을 붙여넣어 주세요",
    model: "모델",
    modelHintList: "연결 시험에서 읽은 목록입니다.",
    modelHintNone: "연결 시험을 누르면 토큰이 쓸 수 있는 모델로 채워집니다.",
    modelPh: "아직 모름",
    test: "연결 시험",
    okDetail: "모델 {n}개 · {ms}ms.",
    stat: {
      unknown: "모름 — 저장된 설정을 읽지 못했습니다",
      address: "저장된 주소",
      empty: "비어 있음",
      set: "걸려 있음",
      modelEmpty: "비어 있음(토큰 기본)",
      test: "이번 연결 시험",
      notRun: "아직 안 돌렸습니다",
    },
    result: {
      ok: { head: "연결됨", body: "모델 목록을 읽었습니다. 아래에서 쓸 모델을 골라 주세요." },
      noModels: {
        head: "허용된 모델이 없음",
        body: "오류가 아닙니다 — 주소도 토큰도 맞습니다. 이 토큰에 allowed_models 가 안 걸린 것이니 ola-mops 관리자에게 등록을 요청해 주세요.",
      },
      unauthorized: {
        head: "토큰이 유효하지 않음",
        body: "주소는 맞습니다 — 서버가 답을 했습니다. API 토큰을 다시 확인하거나 새로 발급해 주세요.",
      },
      notFound: {
        head: "주소를 찾을 수 없음",
        body: "서버는 답했지만 /v1/models 가 없습니다. 주소 뒤에 경로가 더 붙었는지, ola-mops 가 OpenAI 호환 엔드포인트를 켠 판인지 확인해 주세요.",
      },
      httpError: { head: "서버가 오류로 답함", body: "주소와 토큰 문제는 아닙니다. ola-mops 쪽 로그를 확인해 주세요." },
      unreachable: {
        head: "주소에 닿지 못함",
        body: "서버가 답을 아예 안 했습니다 — 404 와는 다른 일입니다. 호스트 이름 · 포트 · 방화벽을 확인해 주세요.",
      },
      noAddress: { head: "주소를 먼저 적어 주세요", body: "서버 주소가 비어 있어 부를 곳이 없습니다." },
    },
  },

  /** git 저장소(#/git) — Gitea 연결과 이 설치에 건 저장소. 시험 결과 말머리는 conn.result 와 같다. */
  git: {
    lead1: "처음 한 번 연결하고 나면 그 뒤로는 저장소만 건드립니다 — 그래서 저장소가 이 화면의 주인공입니다.",
    lead2: "나루가 이슈를 받아 {clone}입니다. 리뷰·머지 트랙은 PR 을 전제해서 여기가 비면 그 두 트랙이 아예 시작되지 않습니다. {runner} — {act} 가 컨테이너가 뜰 때 환경변수로 읽습니다. 웹에 두면 저장 자리가 둘이 되어 갈립니다.",
    lead2Clone: "클론할 곳",
    lead2Runner: "러너 등록 토큰은 이 화면에 없습니다",
    projectsMoved: "리포를 프로젝트로 묶는 것은 {link} 화면에서 합니다.",
    result: {
      ok: "읽을 수 있습니다. 아래에서 걸 것을 골라 주세요.",
      noReposHead: "저장소가 없음",
      noRepos: "오류가 아닙니다 — 주소도 토큰도 맞습니다. Gitea 에 아직 저장소를 안 만들었을 뿐입니다.",
      unauthorized: "주소는 맞습니다 — 서버가 답을 했습니다. 개인 토큰을 다시 발급해 주세요.",
      notFound: "서버는 답했지만 /api/v1 이 없습니다. Gitea 가 아니거나 주소 뒤에 경로가 더 붙었는지 확인해 주세요.",
      httpError: "주소·토큰 문제는 아닙니다. Gitea 쪽 로그를 확인해 주세요.",
      unreachable: "서버가 답을 아예 안 했습니다 — 404 와는 다른 일입니다. 호스트·포트·방화벽을 확인해 주세요.",
    },
    usage: {
      unknown: "몇 건이 쓰는지 아직 모름",
      none: "쓰는 이슈 없음",
      n: "이슈 {n}건이 씁니다",
    },
    tokenSet: "토큰 걸려 있음",
    tokenEmpty: "토큰 비어 있음",
    repoCount: "저장소 {n}개",
    okDetail: "저장소 {n}개 · {ms}ms.",
    notConnected: "연결 안 됨",
    notConnectedHint: "주소를 넣고 「연결 시험」을 눌러 주세요.",
    notConnectedDesc: "주소를 넣고 「연결 시험」을 눌러 주세요. 그 전까지는 저장소 목록을 부를 곳이 없습니다.",
    fix: "연결 고치기",
    addressHint: "Gitea 의 입구입니다. 뒤에 /api/v1 은 붙이지 말아 주세요 — 서버가 붙입니다.",
    token: "개인 토큰",
    tokenHintNone: "아직 없습니다. Gitea 의 설정 > 애플리케이션에서 발급한 값을 적어 주세요.",
    attached: "이 설치에 건 저장소",
    count: "{n}개",
    attach: "저장소 걸기",
    detach: "빼기",
    detachWarn: "빼면 그 이슈들이 「대상 리포 없음」이 됩니다 — 이슈는 남지만 나루가 클론할 곳이 없어 멈춥니다.",
    emptyAttached: "건 저장소 없음",
    emptyAttachedDesc: "읽어 온 목록에서 이 설치가 쓸 것을 골라 주세요. 걸기 전에는 이슈에 붙일 수 없습니다.",
    asking: "제공자에게 물어보는 중…",
    availFailed: "{head} — {err} 연결 띠를 펼쳐 주소와 토큰을 확인해 주세요.",
    availFailedHead: "목록을 읽지 못했습니다",
    giteaEmpty: "Gitea 에 저장소가 없음",
    giteaEmptyDesc: "연결은 됐습니다. 아래 「새 리포 만들기」로 만들면 바로 걸립니다.",
    alreadyOn: "이미 걸림",
    notYetOn: "아직 안 걸림",
    on: "걸림",
    put: "걸기",
    pickerHint: "「＋ 저장소 걸기」를 누르면 연결이 읽어 온 목록이 뜹니다 — 목록은 이 화면에 늘 두지 않습니다.",
  },

  /** git 저장소 › 새 리포 만들기 — 결정 docs/decisions/repository.md 「리포 생성의 주인」. */
  repoCreate: {
    open: "＋ 새 리포 만들기",
    openHint: "Gitea 에 만들고 이 설치에 바로 겁니다.",
    title: "새 리포 만들기",
    owner: "조직",
    ownerHint: "비우면 연결한 계정 아래에 만듭니다.",
    name: "리포 이름",
    nameHint: "영문 · 숫자 · . _ - 만, 100자까지입니다.",
    private: "비공개로 만들기",
    submit: "만들고 걸기",
    made: "{name} 을 만들고 걸었습니다.",
  },

  /** 저장소별 필수 체크(#/git 저장소 줄) — 시안 Figma 26:2. */
  repoChecks: {
    chip: "필수 체크 · {names}",
    chipDefault: "설치 기본",
    chipNone: "필수 체크 없음",
    lead: "병합 전에 초록이어야 할 체크입니다. 나루가 이 이름이 모두 통과할 때까지 기다렸다 병합합니다. 이름은 호스트의 CI 이름 그대로입니다(GitHub 체크 이름 · GitLab 잡 이름 · Bitbucket 상태 이름).",
    default: "설치 기본을 따름",
    defaultDesc: "나루의 B3XIN_NARU_REQUIRED_CHECKS, 비어 있으면 check",
    custom: "이름을 직접 정함",
    none: "요구 없음",
    noneDesc: "체크를 기다리지 않고, 실패한 체크 · 충돌이 있을 때만 막습니다.",
    input: "체크 이름",
    inputPh: "이름을 적고 Enter",
    removeName: "{name} 빼기",
    empty: "이름이 없습니다. 체크를 요구하지 않으려면 「요구 없음」을 골라 주세요.",
  },

  /** 프로젝트(#/projects) — 이슈 · 마일스톤이 서는 자리. 목록 · 상세 문구는 아래 `projects`. */
  project: {
    title: "프로젝트",
    loadFailed: "프로젝트 목록을 불러오지 못했습니다: {err}",
    count: "{n}개",
    name: "이름",
    id: "id",
    idHint: "비우면 자동으로 만듭니다. 이슈 등록이 이 값을 불러 쓰니 바꾸지 않습니다.",
    create: "만들기",
    quickName: "기본 프로젝트",
    quick: "리포 {n}개로 프로젝트 만들기",
    emptyWithRepos: "이슈 · 마일스톤은 프로젝트에 섭니다. 지금 건 리포 {n}개로 바로 만들 수 있습니다.",
    empty: "프로젝트가 없습니다",
    noRepos: "묶은 리포 없음",
    bindLead: "이 설치에 건 리포 중 {name}에 묶을 것을 고릅니다. 누르면 바로 저장됩니다. 리포 하나를 여러 프로젝트에 묶을 수 있습니다.",
    rename: "이름 바꾸기",
    remove: "지우기",
    removeArmed: "한 번 더 눌러 지우기",
  },

  /** git 저장소 › 건 저장소 거르개(t-in-port-work-repos) — 소유자 탭 · 검색 · 연결. */
  repoFilter: {
    owner: "소유자",
    all: "전체",
    noProject: "프로젝트 없음",
    search: "이름 · 주소 검색",
    conn: "연결",
    connAll: "모든 연결",
    count: "{all}개 중 {n}개",
    reset: "거르개 비우기",
    emptyTitle: "이 거르개에 맞는 저장소가 없습니다",
    emptyDesc: "탭 · 검색 · 연결을 바꾸거나 「거르개 비우기」를 눌러 주세요.",
  },

  /** 일감 › 프로젝트(#/projects) — 카드 목록 · 상세(t-in-port-work-projects). */
  projects: {
    lead: "프로젝트는 리포 묶음이고, 이슈 · 마일스톤이 여기에 섭니다. 카드를 누르면 리포를 붙이고 떼는 상세가 열립니다.",
    new: "새 프로젝트",
    facts: "리포 {repos} · 열린 이슈 {open} / {all} · 마일스톤 {ms}",
    emptyDesc: "git 저장소 화면에서 리포를 먼저 붙이면 그 리포로 바로 만들 수 있습니다.",
    back: "프로젝트 목록",
    missing: "프로젝트 {id} 가 없습니다",
    missingDesc: "지워졌거나 id 가 바뀌었습니다. 목록에서 다시 골라 주세요.",
    repos: "리포 {n}",
    bindNone: "이 설치에 건 리포가 없습니다. git 저장소 화면에서 먼저 붙여 주세요.",
    issues: "이슈 현황",
    toBoard: "보드 열기",
    msLine: "마일스톤 {n}개",
    toMilestones: "마일스톤 화면",
    removeNote: "이슈 · 마일스톤이 걸린 프로젝트는 지울 수 없습니다.",
  },

  /** git 호스트 연결 표(#/git 위쪽) — 시안 Figma 22:3. 토큰은 화면에 한 번도 안 나옵니다. */
  gitConn: {
    title: "연결",
    lead: "저장소를 붙이기 전에 git 호스트를 연결합니다. 링크를 열어 호스트에서 승인하면 끝나고, 토큰은 서버에만 남습니다.",
    summary: "연결 {n} · 승인 {ok}",
    add: "연결 추가",
    addTitle: "연결 추가",
    cancel: "닫기",
    table: "git 연결",
    col: { state: "상태", label: "이름", host: "호스트", account: "승인한 계정", repos: "리포", memo: "메모", action: "할 일" },
    state: { ok: "● 연결됨", broken: "! 다시 연결", pending: "◌ 승인 전", legacy: "● 설치 기본", none: "– 없음" },
    legacyLabel: "gitea.json · 읽기만",
    legacyMemo: "연결을 안 고른 리포가 이 자격을 씁니다",
    legacyNone: "설치 기본 자격이 없습니다",
    legacyFix: "고치기",
    autoRefresh: "만료 전에 서버가 알아서 갱신합니다",
    brokenMemo: "{err} — 다시 승인해 주세요",
    connect: "연결 ↗",
    reconnect: "다시 연결 ↗",
    remove: "지우기",
    removeArmed: "한 번 더 누르면 지웁니다",
    removeBlocked: "리포 {n}개가 이 연결을 씁니다 — 리포를 먼저 빼 주세요.",
    unknown: "—",
    github: {
      head: "GitHub — 링크 한 번",
      lead: "GitHub 이 이 설치 전용 앱을 만들고, 고른 리포에 설치하고, 승인까지 이어서 묻습니다. 복사할 값이 없습니다.",
      label: "이름",
      org: "조직 (비우면 내 계정)",
      go: "GitHub 에서 앱 만들고 연결 ↗",
      foot: "GitHub 화면 세 번: 「Create GitHub App」 → 리포 고르고 「Install」 → 「Authorize」. 끝나면 이 화면으로 돌아와 표에 「● 연결됨」이 섭니다. 앱은 고른 리포에만, 코드 · PR · 체크 권한만 받습니다.",
    },
    manual: {
      head: "{host} — 앱 등록 한 번",
      step1: "호스트 주소를 적고 「앱 등록 화면 열기 ↗」",
      step1Cloud: "「앱 등록 화면 열기 ↗」",
      baseUrl: "호스트 주소",
      open: "앱 등록 화면 열기 ↗",
      step2: "열린 화면에 이 두 값을 붙여 넣기",
      redirect: "콜백 주소",
      scope: "범위",
      scopeNone: "호스트 설정을 따릅니다",
      copy: "복사",
      copied: "복사됨",
      step3: "호스트가 준 ID · Secret 을 넣고 「만들고 승인」",
      clientId: "Application ID",
      secret: "Secret",
      label: "이름",
      go: "만들고 승인 ↗",
    },
    back: {
      connected: "연결했습니다. 이제 아래에서 저장소를 붙일 수 있습니다.",
      error: "연결하지 못했습니다 ({kind}). 다시 시도해 주세요 — 호스트에서 거절했거나 링크가 만료됐을 수 있습니다.",
    },
    loadFailed: "연결 목록을 읽지 못했습니다 — {err}",
    pick: "어느 연결에서 가져오시겠습니까?",
    pickLegacy: "설치 기본",
    pickPending: "{label} 연결은 아직 승인 전입니다. 승인하면 그 호스트의 저장소가 여기에 섭니다.",
    pickApprove: "연결 승인 ↗",
    none: "저장소를 붙이려면 git 호스트부터 연결해 주세요.",
  },

  /** MCP 링크 승인(#/oauth/approve) — 시안 Figma 24:2. 토큰은 클라이언트가 직접 받고 이 화면에는 없습니다. */
  oauth: {
    title: "MCP 연결 요청",
    loading: "연결 요청 불러오는 중…",
    ask: "{client} 가 이 설치에 붙겠다고 합니다",
    lead: "허용하면 이 클라이언트가 에이전트 토큰을 받아 이슈를 읽고 쓸 수 있습니다. 내가 방금 연 요청이 맞는지 확인해 주세요.",
    client: "클라이언트",
    redirect: "돌려보낼 곳",
    when: "만료",
    whenText: "{left}",
    notLoopback: "이 컴퓨터 밖의 주소로 돌려보냅니다. 그 서비스를 내가 쓰고 있는지 확인한 뒤 허용해 주세요.",
    name: "토큰 이름",
    nameHint: "영문 소문자 · 숫자 · - · _ 40자까지. 이력과 로그에 이 이름이 남고, 설정 › MCP 토큰에서 이 이름으로 끊을 수 있습니다.",
    approve: "허용",
    deny: "거절",
    approved: {
      title: "{name} 으로 허용했습니다",
      note: "클라이언트로 돌아갑니다. 토큰은 클라이언트가 직접 받아 가고, 이 화면에는 없습니다.",
      desc: "창이 안 넘어가면 이 탭을 닫고 클라이언트로 돌아가 주세요. 설정 › MCP 토큰에서 이 이름으로 보입니다.",
      runners: "MCP 토큰 보기",
    },
    denied: { title: "거절했습니다", desc: "클라이언트에는 「거절됨」이 전해집니다. 토큰은 만들지 않았습니다." },
    missing: { title: "요청을 찾지 못했습니다", desc: "10분이 지나 만료됐거나 이미 처리한 요청입니다. 클라이언트에서 다시 연결하면 새 요청이 열립니다." },
    noRequest: "주소에 요청이 없습니다. MCP 클라이언트가 연 링크로 들어와 주세요.",
  },

  /** 코드 분석(#/code) — mops-code 연결. 띠 · 폼 틀은 git 화면 것을 같이 쓴다. */
  code: {
    lead1: "mops-code 에 어떻게 붙어 있는지 정합니다 — 세션이 코드를 읽을 자리가 여기서 정해집니다.",
    lead2: "mops-code 가 없는 설치도 {normal}입니다 — 비어 있으면 세션은 클론한 리포를 로컬 파일로 읽습니다. 여기서 거는 토큰은 {ro}입니다 — mops-code 에 쓰기 갈래가 없습니다.",
    readOnly: "읽기 전용",
    result: {
      ok: "mops-code 가 답했습니다. 세션에 code MCP 가 붙습니다.",
      noToolsHead: "붙었는데 도구가 없음",
      noTools: "오류가 아닙니다 — 주소도 토큰도 맞습니다. mops-code 가 도구를 하나도 안 내놓습니다 — mops-code 쪽 설정을 확인해 주세요.",
      unauthorized: "주소는 맞습니다 — 서버가 답을 했습니다. 토큰을 다시 확인하거나 mops-code 운영자에게 새로 발급을 요청해 주세요.",
      notFound: "서버는 답했지만 도구 목록이 없습니다. mops-code 가 아니거나 주소 끝에 /mcp 가 빠졌는지 확인해 주세요.",
      httpError: "주소·토큰 문제는 아닙니다. mops-code 쪽 로그를 확인해 주세요.",
    },
    toolCount: "도구 {n}개",
    okDetail: "도구 {n}개 · {ms}ms.",
    address: "주소 — 경로까지",
    addressHint: "mops-code 의 MCP 입구입니다. 뒤에 /mcp 까지 적어 주세요.",
    tokenHintClear: "저장하면 토큰이 지워집니다.",
    tokenHintSet: "저장돼 있습니다. 화면으로 되돌려 주지 않아 값은 안 보입니다 — 바꾸려면 새로 적거나 비워 주세요.",
    tokenHintNone: "아직 없습니다. mops-code 운영자에게 발급받은 값을 적어 주세요.",
    clear: "비우기",
    knows: {
      title: "이 설치가 아는 것",
      mcp: "세션에 code MCP 가 붙습니다 — 연결이 있을 때만입니다.",
      repo: "프롬프트의 「대상 리포」 줄에 owner/name 이 실립니다.",
      index: "색인은 기본 브랜치 기준입니다 — 세션 가지에서 고친 것은 로컬로 읽습니다.",
    },
    emptyNote: "비어 있으면 세션은 로컬 파일로 읽습니다 — 끈 것이 아니라 기본값입니다.",
  },

  /** 로그(#/logs) — 오류 먼저, 그다음 전문. 줄 글자는 서비스가 찍은 그대로다. */
  logs: {
    lead1: "지금 오류가 있는지 먼저 답하고, 그다음에 전문을 보여 줍니다. 같은 오류는 하나로 접습니다.",
    lead2: "{find}입니다 — 흐름을 보려면 {cmd} 가 더 낫습니다. 서비스가 파일에 쓰고 API 가 읽습니다 — {socket}(주는 순간 API 가 호스트 전체를 쥡니다). {admin} 봅니다 — 서비스 로그에는 주소 · 경로가 찍힙니다. 토큰은 애초에 안 찍습니다.",
    lead2Find: "오류를 찾는 화면",
    lead2Socket: "도커 소켓을 안 씁니다",
    lead2Admin: "관리자만",
    callFailed: "{head} — {err} API 가 떠 있는지 확인해 주세요.",
    callFailedHead: "로그를 부르지 못했습니다",
    errors: "오류 {n}",
    warns: "경고 {n}",
    someUnreadable: "{head} — 아래 칩의 설명을 확인해 주세요(경로와 이유가 붙어 있습니다). 파일이 없거나 권한이 없는 것이라, 「오류가 없음」과 뜻이 정반대입니다.",
    someUnreadableHead: "로그를 읽지 못한 서비스가 있습니다",
    groups: "오류 묶음",
    ctxOpen: "앞뒤 20줄 보기",
    ctxClose: "앞뒤 20줄 닫기",
    service: "서비스",
    levelLabel: "수준",
    all: "전체",
    level: {
      error: "오류만",
      warn: "경고+",
    },
    state: {
      ok: "읽음",
      empty: "로그 없음",
      missing: "파일 없음",
      unreadable: "못 읽음",
    },
    empty: {
      blocked: "로그를 읽지 못했습니다",
      blockedDesc: "파일은 있는데 읽지 못했습니다 — 대개 권한 문제입니다. 위 칩에 경로와 이유가 붙어 있습니다. 「로그가 없음」과 뜻이 정반대라, 서비스가 아니라 설치를 확인해 주세요.",
      none: "로그가 없습니다",
      noneDesc: "아직 아무것도 안 찍혔습니다 — 방금 띄웠거나 서비스가 안 돕니다. 위 칩의 경로와 서비스 상태를 확인해 주세요.",
      quiet: "오류가 없습니다",
      quietDesc: "로그는 읽혔고 오류·경고 줄이 없었습니다. 할 일이 없습니다.",
    },
    full: "로그 전문",
    truncated: "{service}: 전체 {total}줄 중 뒤 {read}줄",
    whole: "{service}: 전체 {total}줄",
  },

  /** 디자인 패턴(#/patterns) — 부품을 상태별로 세우는 검토 자리. 견본 자료(이슈 · 알람 · 로그 줄 · 서버 거절 문장)는
   *  서버 · 세션이 줄 글자를 흉내 낸 것이라 여기 두지 않고 그 자리에 copy-ignore 로 남긴다. */
  pat: {
    menu: "디자인 패턴 메뉴",
    side: "절",
    lead: "토큰과 컴포넌트를 실물로 세워 두는 자리입니다. 새로 만든 컴포넌트는 여기 올립니다 — 화면에서 처음 보는 것이 생기지 않게 하는 것이 이 페이지의 일입니다.",
    /** 절 이름 — 레일과 절 제목이 같이 읽는다. */
    nav: {
      type: "타입 스케일",
      color: "색",
      space: "간격 · 라운드 · 그림자",
      spec: "컨트롤 규격표",
      states: "상태 7종",
      form: "폼",
      status: "상태 표식",
      feedback: "알림 · 빈 상태",
      rail: "왼쪽 레일",
      page: "본문 뼈대",
      pwa: "PWA 안내",
      icons: "아이콘",
      chat: "채팅 겉창의 부품",
      issue: "이슈 카드",
      dash: "대시보드의 부품",
      ms: "마일스톤의 부품",
      projects: "프로젝트의 부품",
      ops: "관측 › 지금의 판",
      hist: "관측 › 이력의 부품",
      queue: "일감 › 대기열의 부품",
      graph: "일감 › 그래프의 부품",
      git: "git 저장소의 부품",
      code: "코드 분석의 부품",
      vendors: "배차 벤더의 부품",
      tidy: "이슈 정리의 부품",
      alarm: "알람의 부품",
      logs: "로그 뷰어의 부품",
      auth: "로그인의 부품",
      totp: "2단계 인증의 부품",
      runners: "러너 관리의 부품",
      chores: "게시판의 부품",
      orgs: "조직의 부품",
      mcpTokens: "MCP 토큰의 부품",
      servers: "서버 › 서비스의 부품",
      usage: "사용이력의 부품",
    },
    type: {
      lead: "공통 v3 의 램프입니다(b3x-in C안의 설계값). 배수가 없어 적힌 값이 곧 화면 값입니다 — 글자가 커 보이면 공통 tokens.json 을 줄이자고 올려 주세요.",
      h1: "지금 무엇이 도는가",
      h2: "연결 설정 — ola-mops 주소",
      h3: "이슈 보드 · 목록 · 상세",
      h4: "세션이 방금 끝났습니다",
      h5: "최근 활동",
      h6: "메타 정보",
      body: "지금 전체가 어떤 상태인지 한눈에 봅니다.",
      caption: "3분 전 · 나루 2대 도는 중",
      label: "이슈 등록 · 다시 시도 · 세션 열기",
      eyebrow: "섹션 라벨 · ISSUE · 표 머리글",
    },
    weight: {
      title: "굵기 — 셋, 상한 600",
      w400: "본문 · 설명",
      w500: "단추 · 탭 · 라벨 · 강조 글자",
      w600: "제목 · 섹션 라벨 · 개수",
      tnum: "숫자가 세로로 줄 맞아야 하는 자리(표 · 개수 · 시각)는 고정폭 숫자입니다 — {table} 과 {tnum}.",
      prop: "비례폭",
    },
    color: {
      lead: "공통 v3 의 색(Base UI 값)입니다. 상태색은 둘 — 글자·도형(--x) · 연한 배경(--x-subtle). 채운 위험 단추는 --danger-solid.",
      surface: "표면 사다리 5단",
      surfaceLead: "깊이는 그림자가 아니라 이 사다리로 만듭니다 — 바탕 → 카드·입력 → hover·선택 → 드롭다운·표 머리글 → 눌림. 카드 안에 판 우물도 바탕({canvas})과 같은 층입니다.",
      border: "테두리",
      borderLead: "{strong} 은 장식이라 바탕 대비 3:1 미만입니다. 입력·선택·체크의 유일한 테두리는 {control}(3:1 이상)로 씁니다.",
      ink: "글자 4단",
      glyphs: "가나다 Ag",
      accent: "강조",
      status: "상태색 — 둘씩 8개",
      fg: "--x (글자 · 도형)",
      subtle: "--x-subtle (연한 배경)",
      contrast: "대비는 공통 리포의 check-tokens.mjs 가 다크·라이트 둘 다 잽니다(글자 4.5:1 · 입력 테두리·초점 링 3:1). {tertiary} 는 일부러 4.5:1 에 못 미칩니다 — 비활성·각주 전용입니다.",
    },
    space: {
      title: "간격 · 라운드 · 그림자 · 초점",
      lead: "여백은 공통 간격(--space-*), 글자는 램프(--t-*)로 적습니다. 배수(--u · --f)는 없습니다.",
      space: "간격",
      gutter: "{name} (화면 좌우 여백) = {value} · ≤699px 에서 8px 로 줄어듭니다.",
      radius: "라운드",
      radiusRoles: "라운드 — 자리 이름",
      radiusRolesLead: "화면 CSS 는 위 원시값이 아니라 이 이름을 씁니다 — 컨트롤 · 작은 컨트롤 · 카드 · 떠 있는 층 · 배지.",
      shadow: "그림자",
      shadowLead: "그림자는 「떠 있다」는 신호 하나로만 씁니다({float}). 붙어 있는 카드는 윗변 하이라이트({edge})입니다.",
      iconSize: "아이콘 크기",
      iconLead: "선 굵기 {stroke} = {strokeV} · 움직임은 공통 토큰 — hover·선택 {fast} = {fastV} · 층 등장 {base} = {baseV} · 곡선 {ease}(움직임 줄이기에서 시간 0).",
      focus: "초점 링",
      focusLead: "초점은 {ring} 색의 outline 2px(2px 띄움)입니다 — box-shadow 링은 윈도우 고대비 모드에서 지워집니다. 탭 키로 아래 단추에 들어가 주세요.",
      focusTry: "탭으로 들어와 보기",
      focusAlways: "항상 초점 링",
    },
    spec: {
      lead: "눈금은 셋뿐입니다. 넷째를 만들고 싶어졌다면 규격이 모자란 게 아니라 자리를 잘못 고른 것입니다.",
      step: "눈금",
      height: "높이",
      where: "서는 자리",
      sm: "인라인 — 표 안 · 본문 안 칩 · 줄 끝 아이콘 단추",
      md: "툴바 · 상단 내비 · 카드 안 · 사이드바 줄",
      lg: "폼 — 입력칸 · 드롭다운 · 주 단추",
      lineup: "같은 줄에 세워 보기 — 높이가 맞는지가 전부입니다",
      run: "실행",
      noSm: "입력칸 · 드롭다운은 sm 이 없습니다(표 안에 입력칸을 두지 않습니다)",
      input: "입력칸",
      dropdown: "드롭다운",
      second: "둘째 값",
      chipOn: "칩 켜짐",
      chip: "칩",
    },
    statesSec: {
      title: "상태 7종 — 공통 부품 여덟",
      lead: "단추 · 입력 · 카드 · 배지 · 탭 · 표 · 겉창 · 내비를 기본 · hover · 눌림 · 비활성 · 로딩 · 빈 상태 · 오류로 세웁니다. 그 부품에 없는 상태는 지어내지 않고 「해당 없음」과 이유를 적습니다.",
    },
    buttons: {
      lead: "공통 v3 의 .b-btn — 변형 4 × 상태 3(+ 아이콘 단추). hover·눌림도 공통 것이라 마우스를 올리거나 눌러 확인합니다 — 정지 화면으로 따라 그리면 공통 값을 여기 베끼게 됩니다.",
      variant: "변형",
      iconOnly: "아이콘만",
      ok: "확인",
      sending: "보내는 중",
    },
    form: {
      lead: "라벨 · 입력 · 오류 글이 한 덩이입니다. 오류는 테두리 색이 아니라 글자로 말합니다.",
      olaAddress: "ola-mops 주소",
      olaHint: "예: https://mops.example.internal",
      modelHint: "연결한 뒤에 목록이 채워집니다",
      none: "고르지 않음",
      disabled: "비활성 칸",
      disabledHint: "연결 전에는 고칠 수 없습니다",
    },
    statusSec: {
      lead: "색만으로 가르지 않습니다 — 글리프(모양)와 한글 이름이 늘 함께 갑니다. 흑백으로 봐도 읽혀야 합니다.",
    },
    feedback: {
      title: "알림 · 도는 중 · 빈 상태",
      lead: "셋 다 「무슨 일인지」와 「다음에 뭘 하면 되는지」를 글자로 말합니다.",
      info: "세션이 2건 도는 중입니다.",
      success: "연결됐습니다. 모델 목록을 읽었습니다.",
      warning: "토큰이 30일 뒤 만료됩니다. 새 토큰을 미리 발급해 주세요.",
      error: "주소에 닿지 못했습니다 — 이름을 못 찾음(DNS). 주소를 확인해 주세요.",
      spinner: "움직임을 줄인 환경에서는 멈추지 않고 느리게 돕니다.",
      emptyTitle: "아직 이슈가 없습니다",
      emptyDesc: "먼저 프로젝트를 고르면 그 프로젝트의 이슈가 여기 섭니다.",
      newIssue: "이슈 만들기",
      card: "카드는 제목 · 메타 · 본문 셋으로 섭니다. 카드 안 단추는 규격표의 md 입니다.",
      open: "열기",
      hide: "숨기기",
    },
    rail: {
      lead: "M9 A안 — 하위 보기·필터가 둘 이상인 화면만 레일을 둡니다. 사이드바 안에 서는 것은 이 다섯뿐입니다(SideGroup · SideLink · SideAction · SideCheck · SideNote). 줄은 공통 세로 메뉴(.b-sidenav-item) — 선택은 바탕 · 왼쪽 2px 띠 · 굵게가 함께 갑니다. hover · 초점은 공통 것이라 마우스·탭 키로 확인합니다.",
      rowsLabel: "레일 — 줄 상태",
      rows: "줄 상태",
      selected: "선택 — 띠 · 바탕 · 굵게",
      link: "링크 줄(주소를 바꿉니다)",
      long: "아주 긴 마일스톤 이름은 줄 끝에서 말줄임으로 접힙니다 — 줄 높이는 그대로",
      countLabel: "레일 — 수 · 체크 줄",
      railedLabel: "레일 — 접힘(아이콘만, 이름은 툴팁)",
      noCount: "수를 못 셌습니다 — 자리째 비웁니다",
      checkOn: "켜진 체크 줄",
      checkOff: "꺼진 체크 줄",
      checkOnCount: "켜진 체크 줄 · 수",
      note: "지금 12건 보는 중 · 전체 40건 — 레일 바닥의 사실 한 줄",
    },
    page: {
      lead: "M9 A안(Figma 5-78) — 폭은 내용이 정합니다. 주 화면은 전부 PageHead 로 서고, 목록형 본문은 가득 · 폼형 본문은 FormSplit(폼 열 72ch + 보조 열). 로딩 · 빈 · 오류는 Loading · EmptyState · Alert 를 본문 자리에 그대로 세웁니다.",
      head: "페이지 머리 — 제목만 · 리드 · 제목 옆 사실 · 오른쪽 동작",
      sessionLead: "에이전트 세션이 지금 무엇을 하는지 봅니다. 이슈 상태 변화와 MCP 호출이 아래 이력에 남습니다.",
      lead1: "리드는 문단 여럿이어도 됩니다 — 레일에 있던 설명 글이 여기로 왔습니다.",
      lead2: "둘째 문단은 위와 같은 색 · 같은 간격입니다.",
      form: "폼형 본문 — 폼 열 72ch + 상태 카드 열(≤960px 에서는 아래로)",
      formHint: "입력칸은 폼 열 폭(72ch)까지만 늘어납니다.",
      states: "본문 자리의 상태 셋 — 로딩 · 빈 · 오류",
      emptyDesc: "빈 것은 이유와 다음 할 일을 말합니다.",
      error: "목록을 읽지 못했습니다 — HTTP 502. api 가 떠 있는지 확인해 주세요.",
    },
    pwa: {
      lead: "새 버전 대기 · 서버 연결 안 됨 — 공통 @mops/pwa-v1 이 앱 루트 한 곳에 띄웁니다. 사람이 눌러야만 사라집니다.",
    },
    icons: {
      lead: "번들에 든 인라인 SVG 입니다. CDN 을 부르지 않습니다 — 폐쇄망에서는 조용히 깨집니다.",
    },
    chat: {
      lead: "시안 안 B(Figma 38-2). 단계 띠 · 세션 탭 · 말 줄 · 진행 줄 · 입력칸 · 빈 상태. 표식은 전부 글리프 + 글자입니다.",
      strip: "단계 띠 — 도는 중(●) · 다음(◔) · 끝남(✓) · 실패(✕, 이유 그대로) · 아직(○)",
      tabs: "세션 탭 — 발자국(◇ 사람) · 실패 · 끝남 · 도는 중 · 멈추는 중",
      rows: "대화 줄 — 프롬프트(접힘) · 나루의 말 · 진행(접힘/펼침) · 오류 · 원문 · 사람의 말(전달 상태 셋)",
      footprint: "발자국 줄 — 사람 세션(Roo Code)의 대화 자리",
      input: "입력칸 — 도는 중 · 멈추는 중 · 보내는 중 · 발자국 탭(멈춤 없음) · 비활성",
      stoppingHint: "멈춤을 요청했습니다 — 나루가 다음 박동에 끊습니다.",
      empties: "빈 상태 셋 — 세션 없음 · 로그 없음 · 못 읽음",
      noSessionsDesc: "아직 아무 세션도 안 돌았습니다. 이슈를 가리키거나 Roo Code 로 붙어서 일하면 여기 남습니다.",
      noLogDesc: "세션은 있는데 줄이 0 입니다 — 방금 열렸거나, 나루가 밀기에 실패하고 있습니다(나루 로그를 확인해 주세요).",
      logError: "로그를 읽지 못했습니다 — HTTP 502 — /api/agent-sessions/s3/log. API 가 떠 있는지 확인해 주세요.",
    },
    projects: {
      lead: "일감 › 프로젝트의 부품입니다. 카드를 누르면 상세가 열리고, 리포 묶기는 체크하면 바로 저장됩니다(여기서는 서버를 부르지 않습니다).",
      cards: "카드 목록 — 이름 · id · 붙은 리포(한 줄, 넘치면 말줄임) · 리포 · 열린 이슈 / 전체 · 마일스톤",
      form: "새 프로젝트 폼 — 이름 필수 · id 는 비우면 서버가 만듭니다",
      empty: "빈 상태 — 리포가 있으면 한 번에 만들기 · 리포도 없음",
      detail: "상세 — 이슈 현황(값 · 없음) · 리포 묶기(관리자 · 읽기 전용 · 걸 리포 없음)",
    },
    ops: {
      lead: "관측 › 지금(세션 진행)의 판입니다. 표는 좁은 폭에서 카드로 접지 않고 가로로 구릅니다. 못 읽은 토큰 · 모델은 「모름」입니다.",
      usage: "토큰 사용량 — 묶음 탭 · 요약 숫자 · 표(모름 섞임)",
      usageEdge: "사용량 가장자리 — 1,000건 상한에 걸림 · 기간 안 세션 없음",
      locked: "잠긴 이슈 — 세션이 쥠 · 잠가 둠(풀기 도는 중) · 없음",
      naru: "나루 — 붙음 · 끊김 · 안 붙어 봄 · 「도는 것만」에 남는 게 없음",
      sess: "세션 이력 — 도는 중(토큰 모름 · 계속 자람) · 끝남 · 실패",
      ev: "이벤트 조회 — 상태 변경 · 발자국 · 맞는 것 없음",
      chart: "단계별 토큰 누적 막대 — 공통 차트 모듈(@mops/charts-v2)",
      stages: "이슈마다 단계별로 — 값 · 모름 · 세션 없음(—)",
      save: "무엇을 줄일까 — 사실 줄 · 없음",
    },
    hist: {
      lead: "관측 › 이력의 부품입니다. 값이 없는 줄(모름 · 시각 모름)은 정렬 방향과 상관없이 늘 뒤로 갑니다. 평균의 분모는 값이 있는 이슈 수입니다.",
      bar: "거르개 줄 · 합계/평균 · 표 — 눌러 볼 수 있습니다",
      empty: "빈 상태 — 맞는 끝난 이슈 없음",
    },
    queue: {
      lead: "일감 › 대기열의 부품입니다. 순서와 사유 코드는 서버가 주고, 화면은 코드를 문장으로만 바꿉니다. 모르는 코드는 그대로 보입니다.",
      rows: "줄 — 다음 차례 · 집을 수 있음 · 사유별 기다림 · 모르는 코드",
      empty: "빈 상태 — 자동 실행을 켠 이슈 없음",
    },
    graph: {
      lead: "일감 › 그래프의 부품입니다. 선행은 실선 화살표, 하위는 점선입니다. 상태는 왼쪽 띠 색과 상태 표식(모양 · 글자)이 함께 말합니다.",
      open: "하위 펼침 · 찾은 노드 강조 · 상태별 띠 — + / − 를 눌러 볼 수 있습니다",
      shut: "하위 접힘 · 완료 숨김 — 접힌 하위의 선행은 보이는 조상으로 옮겨 붙습니다",
    },
    ms: {
      lead: "마일스톤 화면의 조각입니다. 넣기/빼기 막대는 공통 .b-bulkbar 이고, 고른 것 중 마일스톤 밖의 것은 넣기 · 안의 것은 빼기로 셉니다.",
      info: "진행률 — 값 · 이슈 없음",
      list: "이슈 체크 목록 — 고른 상태 · 빈 상태",
      bulk: "넣기/빼기 막대",
      form: "새 마일스톤 폼 — 기본 · 서버 거절",
    },
    dash: {
      lead: "대시보드의 조각입니다. 견본 이슈를 화면과 같은 함수로 접어 그립니다 — 나루 카드는 도는 중 · 노는 중 · 끊김 · 아직 안 붙음 · 모름을 글리프와 글자로 가릅니다.",
      kpi: "지표",
      naru: "나루 카드 — 관리자(러너 표 있음) · 세션만 · 빈 상태",
      progress: "진행률 — 값 · 셀 이슈 없음",
      attention: "주의 필요 — 줄 · 빈 상태",
      projects: "프로젝트 카드 — 견본 · 빈 상태",
    },
    issue: {
      lead: "보드 카드. 표식은 넷 다 색이 유일한 신호가 아닙니다 — 테두리 두께 · 글리프 · 글자가 함께 갑니다.",
      acts: "상세의 조작 줄(Figma 28:2 · 31:2 · 33:2) — 기본 · 세션이 쥐고 막힘 · 도는 중 · 에픽 · 취소됨 · 오류 · 리뷰에서 막힘 · 스펙 빈 할 일, 그리고 취소 확인 · 넘기기 창 · 단계 옮기기 메뉴",
      edit: "상세의 고치는 자리(Figma 31:2) — 스펙 편집 · 열린 질문 · 막힘 보완 · 선행 · 하위(빈 · 있음), 보드 선택 줄 · 삭제 확인",
      tabs: "상세의 탭(Figma 33:2) — 의도 · 완료 기준 · 정보 · 인계 · 이력, 그리고 빈 상태",
      detail: "상세의 틀 — 메타 줄(도는 세션 · 비어 있음)과 주소 복사입니다. 모달 머리의 ‹ › 는 지나온 길이 없으면 꺼집니다.",
    },
    git: {
      lead: "채택 시안 안 C(Figma 46-2) · 공통 프레임 47-2. 시험 일곱 갈래 · 연결 띠 · 저장소 줄 · 빈 상태 셋. 문구는 화면과 같은 것을 읽습니다 — 여기서 다시 적으면 두 벌이 됩니다.",
      // copy-ignore: 「!」 는 느낌표가 아니라 화면의 경고 글리프(!) 이름이다.
      checks: "저장소별 필수 체크 — 칩 셋(설치 기본 · 없음 · 이름들)과 펼친 편집(Figma 26:2)",
      filter: "건 저장소 거르개 — 소유자 탭(전체 · owner · 프로젝트 없음) · 이름/주소 검색 · 연결 · 남은 수",
      filterOn: "거르개를 건 상태 — 「거르개 비우기」가 나타납니다",
      conns: "연결 표 — 연결됨 · 다시 연결 · 승인 전 · 설치 기본, 「연결 추가」는 표 위에서 펼칩니다(Figma 22:3)",
      test: "연결 시험 — 일곱 갈래(✓ 됨 · ! 경고 · ✕ 오류 · · 아직)",
      testNote: "「저장소가 없음」은 {notError} — 갓 세운 Gitea 의 정상 상태라 빨갛게 칠하면 사람이 멀쩡한 주소부터 다시 봅니다. 「닿지 못함」과 「찾을 수 없음」도 다른 갈래입니다(404 는 서버가 답을 한 것입니다).",
      notError: "오류가 아닙니다",
      strip: "연결 띠 — 접힌 채로 섭니다(연결됨 · 연결 안 됨)",
      rows: "저장소 줄 — 건 것(■) · 아직 안 건 것(□) · 「몇 건이 쓰는지 모름」",
      nullNote: "{null} 은 {unknown}입니다 — 0 으로 적으면 「세어 봤더니 없음」과 구분이 안 되고, 그 둘은 사람이 할 일이 정반대입니다.",
      nullUnknown: "0 이 아니라 「모름」",
      empties: "빈 상태 셋 — 서로 달라야 합니다",
      create: "새 리포 만들기 — 접힘 · 펼침 · 서버 거절(빠진 범위 이름을 그대로)",
    },
    code: {
      lead: "채택 시안 안 B(Figma 88-42). GitPage 의 접힌 띠 틀을 그대로 씁니다(git__* 클래스) — 저장소 목록 대신 이 설치가 아는 것을 보여 줍니다. 저장 주소는 이미 완성된 MCP 문(…/mcp 까지)이라 화면이 경로를 더 안 붙입니다.",
      testNote: "「붙었는데 도구가 없음」은 {notError} — 주소도 토큰도 맞습니다. mops-code 쪽 설정이 빈 것뿐입니다. 「찾을 수 없음」과 「닿지 못함」도 다른 갈래입니다(404 는 서버가 답을 한 것입니다).",
      strip: "연결 띠 — 기본(접힘, 연결됨 · 연결 안 됨) · 시험 중",
      testing: "시험 중 — 단추가 돌고 결과 배너는 비웁니다.",
      tokens: "토큰 줄 — 걸려 있음(비우기 가능) · 안 걸림 · 지울 예정",
      knows: "이 설치가 아는 것 · 빈 상태 — 꺼진 것이 아니라 기본값",
    },
    vendors: {
      lead: "채택 시안 안 A(Figma node-id=50-3, 표 하나). 벤더 축은 세 층입니다 — 웹/DB(이 표) > env(.env 기본값) > 안 고름. 여기서 지운 것과 애초에 안 고른 것은 같은 값입니다.",
      table: "단계별 벤더 표 — 안 고름(env 기본값) · 고름 · 이 빌드에 꽂힌 이름만",
      tableNote: "「이 빌드에 꽂힌 이름」만 드롭다운에 뜹니다 — 폐쇄망 빌드에는 claude·codex 가 없습니다. 그래도 드롭다운에 없는 이름을 env 로 적어 뒀다면 그건 다른 검사(`Unknown`)가 잡습니다.",
      saveBar: "저장줄 — 고친 것이 있을 때만 뜹니다(ConnPage 와 같은 규약)",
      noFallback: "조용한 폴백 금지",
      noFallbackNote: "고른 벤더가 그 트랙을 못 맡으면 다른 벤더로 대신 돌리지 않습니다 — 대신 돌면 사람은 화면에서 고른 것이 도는 줄 알고, 그 착각은 결과물을 봐도 안 드러납니다(`PickFail`, `docs/decisions/naru-dispatch.md`).",
    },
    tidy: {
      lead: "채택 시안 안 A(Figma 52-3) · 공통 55-2. 세 단추가 세 자리에 있습니다 — 정리 요청(범위 줄) · 적용(표 바닥 막대, 사람만) · 기각(줄 끝). 표식은 전부 글리프 + 글자입니다.",
      kinds: "갈래 · 제안 상태",
      scope: "범위 줄 — 기록 없음 · 쉬는 중 · 요청됨 · 도는 중 · 못 읽음",
      result: "적용 결과 — 늘 두 숫자 · 사유는 서버 문장 > 화면 표 > 코드 그대로",
      table: "제안 표 — 둘 고름 · 지난 기록 · 목록에 없는 이슈 · 모르는 갈래",
      applying: "적용 중 — 단추가 돌고 고르기가 잠깁니다",
      confirm: "적용 확인 — 막대 안에서 한 번 더 묻습니다 · 긴 근거는 세 줄로 접힙니다",
      states: "상태 — 로딩 · 오류 · 훑었는데 없음 · 한 번도 안 훑음",
    },
    alarm: {
      lead: "채택 시안 안 A(Figma 58-3) · 공통 60-2. 종의 숫자는 목록을 세지 않고 GET /alarms/unread 로 따로 받습니다. 안 읽음은 셋이 함께 — 왼쪽 띠 · ● · 굵은 본문. 갈래는 글리프 + 글자.",
      bell: "종 — 주의 있음 · 안 읽음만 · 없음 · 아직 안 읽음 · 못 읽음 · 열림",
      chips: "갈래 칩 — 여섯 · 모르는 갈래",
      popover: "팝오버 — 안 읽음 셋 · 읽음 · 이슈가 지워진 줄",
      row: "한 줄 — 누르기 전(안 읽음) · 누른 뒤(읽음) · 러너 끊김 · 다시 붙음(끝남, 흐림)",
      table: "알람 표 — 둘 고름(바닥 막대가 섭니다) · 고른 것 없음(막대 없음)",
      marking: "읽음 남기는 중 — 단추가 돌고 고르기가 잠깁니다",
      states: "상태 — 로딩 · 오류 · 없음 · 거르개에 맞는 것 없음",
    },
    logs: {
      lead: "채택 시안 안 A(Figma 29-2). 표식은 셋입니다 — 글리프 · ERROR/WARN 글자 · 배경 한 단. 흑백으로 봐도 줄이 구분되는지 여기서 봅니다.",
      summary: "요약 줄 — 「지금 오류가 있나」에 한눈에 답하는 자리",
      chipsNote: "서비스별 칩은 {four} — 읽음 · 로그 없음 · 파일 없음 · 못 읽음. 넷 다 사람이 할 일이 다르고, 특히 「없음」과 「못 읽음」은 정반대입니다.",
      chipsFour: "넷을 안 접습니다",
      filters: "거르개 — 서비스 · 수준",
      serviceSample: "서비스(보기)",
      levelSample: "수준(보기)",
      groups: "오류 묶음 — 같은 오류는 하나로 접고 횟수를 적습니다",
      full: "로그 전문 — 오류 줄만 배경 한 단, 못 읽은 줄은 흐리게(버리지 않습니다)",
      unknownNote: "수준을 못 읽은 줄(역추적의 본문 등)은 {keep} — 오류의 본문이 대개 거기 있습니다.",
      keep: "버리지 않습니다",
      ctx: "앞뒤 20줄 — 거르개를 지나기 전 원본에서 뜹니다",
      ctxNote: "가운데 줄({at})이 {that}입니다 — 앞뒤가 다 비슷해 보이면 어느 것인지 모르니 색만 쓰지 않고 글리프(▸)와 왼쪽 띠를 같이 줍니다. 위아래의 INFO·DEBUG 는 {low}: 이 목록을 걸러진 줄에서 잘라 쓰면 오류 앞뒤의 INFO 가 통째로 빠지고, 원인은 대개 그 줄에 있습니다.",
      ctxThat: "눌렀던 그 오류",
      ctxLow: "고른 수준보다 낮아도 옵니다",
      empties: "빈 상태 셋 — 셋 다 사람이 할 일이 다릅니다",
      noneDesc: "아직 아무것도 안 찍혔습니다 — 방금 띄웠거나 서비스가 안 돕니다. 서비스를 확인해 주세요.",
      unreadable: "{head} — 파일이 없거나 권한이 없습니다. 경로와 이유가 칩에 붙습니다.",
    },
    auth: {
      lead: "채택 시안 안 B(Figma 90-58) · 공통 ①~④. 실패는 서버 문장 그대로입니다 — 화면이 문장을 바꿔 쓰지 않습니다. 판정은 전부 서버가 합니다.",
      login: "로그인 — 기본 · 안내 · 실패 · 도는 중",
      modes: "로그인 방식별 — sso · local+sso(접힘 · 펼침) · hub 에서 돌아오다 실패",
      setup: "첫 설정 — 계정 0개 + 설치 토큰일 때만",
      menu: "계정 메뉴 — 단추 · 펼친 팝오버",
      account: "내 계정 — 정보 · 비밀번호(기본 · 됨 · 실패 · 비밀번호 없음)",
      users: "설정 › 사용자 — 표(펼친 줄 · 거절) · 새 계정",
      usersScope: "사용자 — 펼친 줄의 이메일 · 범위(일반: 프로젝트 고름 · 저장함 / 관리자: 범위 잠김)",
    },
    usage: {
      lead: "대시보드 맨 위 카드(채택 시안 A안)입니다. 막대는 공통 @mops/charts-v2 가 그립니다 — 칸은 서버가 다 채워 주고 화면은 계산하지 않습니다. 0 과 「모름」은 다른 판입니다.",
      normal: "정상 — 1일 · 범례로 끄고 켜기 · 올리면 툴팁",
      behind: "뒤처짐 — 막대는 그리되 끝 칸이 적을 수 있다고 말합니다",
      empty: "비었음 — dbix 가 답했고 칸이 전부 0",
      loading: "불러오는 중",
      notConfigured: "연결 안 됨 — 설정이 비었음",
      unknown: "모름 — dbix 에 닿지 못함",
    },
    servers: {
      lead: "서버 화면(Design 캔버스 「나루 서비스 화면 시안」 Main · Add)입니다. 서비스 상태는 모양 · 글자 · 색이 함께 가고, 보고가 없으면 「모름」이지 0개가 아닙니다.",
      mark: "서버 표식 — 붙음 · 끊김 N분째 · 끊김 N일째 · 안 붙음(끊긴 시각 모름)",
      table: "서비스 표 — 도는 중 · 멈춤(도메인 + IP) · 세팅 전(목록 값) · 받기 전 · 웹 없음(나루)",
      empty: "보고 없음 · 등록된 서비스 없음",
      form: "리포 추가 폼 — 기본 · 서버 거절 · 보내는 중",
      jobs: "리포 추가 요청 — 대기 · 진행 · 막힘 · 완료",
    },
    mcpTokens: {
      lead: "설정 › MCP 토큰입니다. 러너 표와 같은 모양(표 · 위에 펼치는 폼 · 한 번만 보이는 카드)이고, 한 줄 단추는 「관리」 하나입니다. 만료됨은 ⚠ 글자와 함께 갑니다.",
      table: "표 — 무제한 · 날짜 만료 · 만료됨 · 범위 전체 · 범위 고름 · 아무것도 못 봄 · 안 써 봄",
      empty: "빈 상태",
      form: "발급 폼 — 기본 · 서버 거절",
      edit: "관리 카드 — 기본 · 폐기 확인 대기",
      token: "발급 직후 한 번만 보이는 토큰 카드",
    },
    orgs: {
      lead: "관리 › 조직입니다. 역할 배지는 아이콘과 글자가 함께 가고, 카드 한 장의 단추는 「관리」(읽기만이면 「보기」) 하나입니다. 무엇을 그릴지는 역할이 정합니다 — 관리자는 전부, OWNER 는 이름 · 멤버, USER · GUEST 는 읽기만.",
      guide: "역할 설명 표",
      cards: "조직 카드 — 한 사람이 조직마다 다른 역할(OWNER · USER · GUEST) · 관리자인데 멤버 아님 · 고른 카드",
      new: "새 조직 폼 — 기본 · 이름 겹침",
      manageAdmin: "관리 판 — 관리자(git 연결 고르기 · 프로젝트 배정 · 삭제)",
      manageOwner: "관리 판 — OWNER(이름 · 멤버) · 마지막 OWNER 거절",
      manageRead: "관리 판 — USER · GUEST(읽기만)",
      edge: "빈 조직 · 삭제 확인 대기 · 멤버 없음 + 더하는 중",
    },
    chores: {
      lead: "관리 › 게시판입니다. 에이전트 · 사람 배지는 아이콘과 글자가 함께 가고, 끝낸 줄은 취소선 + ✓ 글자로 가릅니다. 한 줄의 단추는 「수정」 하나이고 삭제는 수정 폼 안에서 두 번 누릅니다.",
      form: "새 항목 폼 — 기본 · 서버 거절",
      rows: "줄 — 에이전트가 남김(메모 · 문서 경로) · 사람이 남김(링크) · 끝냄 · 수정 중",
      list: "목록 — 남은 일 + 끝낸 일 접힘 · 남은 일 0",
    },
    runners: {
      lead: "설정 › 러너(채택 시안 A안, Figma 15-3)입니다. 붙음 · 끊김 · 안 붙어 봄은 모양 · 글자 · 색이 함께 가고, 붙지 않은 러너의 태그 · 벤더 · 판은 「모름」입니다.",
      table: "표 — 붙음(둘 · 호스트) · 끊김 N분째 · 안 붙어 봄 · 설정 파일(읽기만 · 도커) · 폐기 확인 대기",
      tableErr: "표 — 서버 거절 문장 그대로",
      empty: "빈 상태",
      form: "추가 폼 — 기본 · 거절 · 보내는 중",
      token: "발급 직후 한 번만 보이는 토큰 카드",
      pairBar: "툴바 — 코드로 연결 · 토큰 직접 발급",
      pairReq: "연결 요청 카드 — 기본 · 이름 겹침(웹) · 서버 거절 · 보내는 중",
      pairEnd: "연결 요청의 끝 — 허용됨 · 거절함 · 코드 없음/만료",
      mcpAsk: "MCP 연결 요청 — 루프백 · 이 컴퓨터 밖으로 돌려보냄(Figma 24:2)",
      mcpEnd: "MCP 연결 요청의 끝 — 허용됨 · 거절함 · 없음/만료",
    },
    totp: {
      lead: "안쪽 모양은 공통 @mops/totp-ui-v2 그대로(mops-code 와 같은 조각) — 여기는 바깥 카드만 두릅니다. 조각이 공통 v3 어휘(.b-btn · .b-input …)를 그대로 써서 이름을 잇는 다리가 없습니다.",
      login: "로그인 2단계 · 등록 강제",
      account: "내 계정 — 미등록 · 등록됨 · 정책이 필수",
      policy: "설정 › 로그인 · 2단계 — 강제 정책",
    },
    sample: {
      label: "표본",
      patterns: "패턴",
      locked: "잠김",
      tokenEmpty: "토큰이 비어 있습니다. 발급한 값을 적어 주세요.",
      epic: "에픽",
      fontLoad: "글꼴 싣기",
      navOverflow: "내비 넘침",
    },
    /** 상태 7종 판. */
    states: {
      base: "기본",
      pressed: "눌림",
      disabled: "비활성",
      loading: "로딩",
      empty: "빈 상태",
      na: "해당 없음",
      liveHover: "마우스를 올려 주세요",
      livePress: "마우스를 눌러 주세요",
      times: "회수",
      button: "단추",
      input: "입력",
      focusInstead: "눌림 대신 초점 — 칸을 눌러 주세요",
      listLoading: "목록 불러오는 중…",
      card: "카드",
      cardTitle: "연결",
      cardEmpty: "아직 연결이 없습니다",
      cardEmptyDesc: "주소와 토큰을 넣으면 여기 섭니다.",
      cardError: "주소에 닿지 못했습니다 — DNS. 주소를 확인해 주세요.",
      badge: "배지",
      tab: "탭",
      sessionsLoading: "세션 불러오는 중…",
      sessionsError: "세션 목록을 읽지 못했습니다 — HTTP 502. 다시 열어 주세요.",
      table: "표",
      tableEmpty: "이슈가 없습니다",
      tableEmptyDesc: "거르개를 풀어 주세요.",
      tableError: "목록을 읽지 못했습니다 — HTTP 502. 새로고침해 주세요.",
      dock: "겉창",
      dockLabel: "채팅 겉창 표본",
      dockFoot: "도는 세션이라 말을 걸 수 있습니다.",
      dockEnded: "끝난 세션이라 입력칸이 없습니다 — 「발자국」 탭에서 남겨 주세요.",
      noSessions: "세션이 없습니다",
      noSessionsDesc: "아직 아무 세션도 안 돌았습니다.",
      logError: "로그를 읽지 못했습니다 — HTTP 502. 다시 열어 주세요.",
      nav: "내비",
      navMenuTitle: "내비 메뉴 — 테마(t-in-shell-nav-overflow)",
      myTurnTitle: "내 차례 서랍(t-in-port-shell)",
      myTurnLead: "상단 바 서랍 단추가 여는 판입니다. 답할 질문 · 내가 맡아 둔 이슈 두 묶음이고, 줄을 누르면 보드에서 그 이슈가 열립니다. 왼쪽부터 채움 · 빈 · 로딩 · 오류입니다.",
      myTurnErr: "b3x-in api 에 닿지 못했습니다",
      myTurnQTitle: "배포 키를 어디에 둘지 정해 주세요",
      myTurnQPrompt: "CMMN_DEPLOY_KEY 를 저장소 시크릿으로 두시겠습니까?",
      myTurnMineTitle: "로그 뷰어 필터 줄 정리",
      navMenuLead: "테마는 아이콘 단추 하나 + 메뉴(공통 .b-menu)입니다. 주 메뉴는 묶음 다섯(대시보드 · 일감 · 관측 · 관리 · 디자인 패턴)이라 넓은 화면에서 접을 탭이 없습니다. 959px 이하는 공통 서랍(묶음 이름 아래 화면 전부), 767px 이하는 하단 탭바입니다. 아래는 폭과 무관하게 펼친 표본입니다.",
      /** 「해당 없음」 칸의 이유 — 그 상태가 왜 없는지와 무엇이 대신하는지. */
      na_: {
        btnEmpty: "단추는 비지 않습니다 — 할 일이 없으면 단추를 안 그립니다.",
        btnError: "단추가 실패를 말하지 않습니다 — 결과는 옆의 오류 알림(Alert)이 말합니다.",
        cardHover: "판은 누르지 않습니다. 누르는 카드는 이슈 카드(아래 「이슈 카드」 절) — hover 는 --surface-2.",
        cardPress: "판은 누르지 않습니다(위와 같습니다).",
        cardDisabled: "판은 잠기지 않습니다 — 잠긴 것은 안의 컨트롤이 말합니다.",
        badgePress: "배지는 표식이라 누르지 않습니다.",
        badgeDisabled: "배지는 잠기지 않습니다. 닫힌 이슈는 「⊘ 취소」 표식이 따로 말합니다(흐리게만 그리면 완료와 구분이 안 됩니다).",
        badgeEmpty: "수가 0 이면 수 배지를 아예 안 그립니다 — 「0」 과 「못 읽음」 은 다릅니다.",
        tablePress: "표 줄은 눌러도 모양이 안 바뀝니다 — 누르면 고른 줄(기본 칸의 파란 줄)이 됩니다.",
        dockPress: "겉창 판은 누르지 않습니다 — 닫기 단추는 단추 줄이 말합니다.",
        navDisabled: "내비 칸은 잠그지 않습니다 — 권한이 없는 칸은 아예 안 그립니다(관리자 칸).",
        navLoading: "내비는 읽지 않습니다. 종의 수를 읽는 동안에는 수를 안 적습니다(0 이 아닙니다).",
      },
    },
  },
} as const;
